import React, {
    useState,
    useEffect,
    useLayoutEffect,
    useRef,
    useCallback,
    useMemo,
    useSyncExternalStore,
} from 'react'
import LeftSidebar from '../shared/Sidebar/LeftSidebar'
import FilterOptions from 'components/filters/FilterOptions'
import PublicFilterOptions from 'components/filters/PublicFilterOptions'
import Control from 'components/panels/Control'
import PublicControl from 'components/panels/PublicControl'
import { isMobile, isAndroid, isIOS } from 'lib/device'
import { fixedHeaderEnabled } from 'lib/fixedHeader'
import PanelEdgeChevrons from 'components/panels/PanelEdgeChevrons'
import { resolveBottomEditorInset, resolveBottomControlsUnderEditor } from 'lib/bottomEditor'
import { hideThreadNavbarEnabled, shouldHideThreadNavigation } from 'lib/threadNavigation'
import { readRememberedKeyboardHeight, rememberKeyboardHeight } from 'lib/keyboardHeightMemory'
import { resolveKeyboardPhase } from 'lib/bottomEditorGlide'
import {
    bottomEditorDockEnabled,
    getBottomEditorDock,
    inactiveBottomEditorDockSnapshot,
} from 'lib/bottomEditorDock'
import {
    getKeyboardEdgeAdapter,
    keyboardEdgeActive,
    keyboardEdgeTrackerEnabled,
    rememberedKeyboardEdgeCover,
    resolveExpectedEdgeCover,
    shellOffersKeyboardEdge,
} from 'lib/keyboardEdgeAdapter'
import { postMessageToNative } from 'utils/nativeBridge'
import { setDocumentTitleForPath, setTitleNotificationCount } from 'utils/documentTitle'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Virtual } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/virtual'
import { DndProvider } from 'react-dnd'
import MultiBackend, { TouchTransition, MouseTransition } from 'react-dnd-multi-backend'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { TouchBackend } from 'react-dnd-touch-backend'
import Preview from 'react-dnd-preview'
import Answer from '../answers/Answer'
import PanelContainer from 'components/panels/PanelContainer'
import Panel from 'components/panels/Panel'
import EmptyPanel from 'components/panels/EmptyPanel'
import HistoryPanel from 'components/panels/HistoryPanel'
import { isLocalCachedQuest } from 'components/panels/questDisplay'
import useStore, { startingIndex, resolveEffectiveTheme } from 'state/knovStore'
import {
    getSwiperRef,
    getCenterIndex,
    getCenterPanel,
    completeOnePanelPrewarm,
    finishOnePanelGesturePrewarm,
    prewarmOnePanelGesture,
    slideTo,
    SWIPE_SPEED_FAST,
    SWIPE_SPEED_SLOW,
} from 'state/imperativeApis/swiperApi'
import AutocompletePopup from 'components/AutocompletePopup'
import ErrorBoundary from 'components/shared/ErrorBoundary'
import CableApp from '../../actioncable'
import { withChannelAuth } from 'state/channels/channelAuth'
import { uniqBy } from 'lodash'
import * as cache from 'state/cache'
import styles from 'components/PanelController/panel-controller.module.scss'
import { throttle } from 'lodash'
import { pathFromFilter } from 'components/filters/useStreamFilters'
import { getPanelBackStack, recordCenteredPanelVisit } from 'state/panelBackStack'
import api from 'api/api'
import cn from 'classnames'
import SidebarButton from 'components/shared/SidebarButton'
import TDP from 'components/shared/TDP'
import MobileConsole from 'components/devtools/MobileConsole'
import GlobalAnswersDndProvider from 'components/answers/dndKit/GlobalAnswersDndProvider'
import TreechatTooltip from 'components/shared/TreechatTooltip'
import WithTooltip from 'components/shared/WithTooltip'

import useSlideEffects from 'refactor/hooks/useSlideEffects'
import useAnimatedSlide from 'refactor/hooks/useAnimatedSlide'
import WalletControl from 'components/panels/WalletControl'
import UserControl from 'components/panels/UserControl'
import { transformTo, SMALL, BIG } from 'lib/transform'
import { playPanelRowShift } from 'lib/panelRowShift'
import useWalletDetails from 'api/useWalletDetails'
import WalletBalanceAmount from 'components/wallet/WalletBalanceAmount'
import { BsvWalletType } from 'wallets/wallets'
import RightSidebar from '../shared/RightSidebar'
import NewQuestButton from 'components/quests/NewQuestButton'
import NewQuestButtonUIMainWrapper from 'components/quests/NewQuestButtonUIMainWrapper'
import { isOrdinalsMintEnabled } from 'components/quests/mintMediaThreadPolicy'
import { redirectToLoginScreen } from '../../AppRoot'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { isIMEComposing, getEditorRef, blurFocusedNewAnswer } from 'state/imperativeApis/editorApi'
import queryClient from 'api/queryClient'
import { markStartupMetric } from 'lib/startupMetrics'
import {
    getPanelNavInputDetail,
    markPanelNavFirstPanelMove,
    markPanelNavQuestSettled,
    markPanelNavRouteChange,
    markPanelNavTransitionEnd,
    markPanelNavTransitionStart,
    startPanelNavMetric,
} from 'lib/panelNavMetrics'
import { cacheQuestPreservingPendingOptimisticAnswers } from 'state/pendingOptimisticAnswers'
import { pausePanelMediaLeavingSwiperViewport } from 'components/panels/panelMediaPlayback'
import { PANEL_SWIPE_EXCLUSION_SELECTOR } from 'components/shared/panelSwipeExclusions'
import { hasPanelInDirection, playPanelBoundaryFeedback } from 'lib/panelBoundaryFeedback'
import {
    buildKeyboardDismissingMessage,
    buildKeyboardOpeningMessage,
    isKeyboardInputElement,
    isReplyComposerFocusTarget,
    resolveFootKeyboardState,
    resolveKeyboardDismissalAnticipation,
    resolveViewportKeyboardOpen,
    shouldExpectKeyboardOnFocus,
    NATIVE_KEYBOARD_CHANGE_EVENT,
    normalizeNativeKeyboardState,
    reconcileNativeKeyboardState,
    readNativeKeyboardState,
    resolveKeyboardState,
    resolveIosKeyboardReserve,
    canPredictKeyboardOpening,
    resolveKeyboardExpectationTimeoutMs,
    viewportResizesForKeyboard,
    shouldEndKeyboardExpectationOnNativeReport,
    resolvePanelKeyboardHeight,
    resolveViewportKeyboardHeight,
    shouldBlurNewAnswerAfterKeyboardClose,
    shouldHideBottomNavigation,
    shouldObserveViewportKeyboard,
    shouldRevealNewAnswerOnKeyboardOpen,
    shouldRetainReplyComposerKeyboardFallback,
    shouldUseViewportKeyboardFallback,
} from 'components/PanelController/mobileKeyboardFocusBehavior'
import { createPanelNavSideEffectQueue } from 'components/PanelController/panelNavSideEffects'
import { deliverSwiperTouchEndFromElement } from 'components/PanelController/swiperElementTouchEnd'

let initialInnerHeight = window.innerHeight
const KEYBOARD_FOCUS_FALLBACK_MS = 1000
// How long the bottom chrome is still settling after the keyboard goes: the
// navbar's rise from the edge and the composer pill's glide up onto it
// (control.module.scss mobileControlReturn, panel-container.module.scss
// .bottomEditorHostKeyboard) run 220ms; the flag outlives them slightly.
const KEYBOARD_SETTLE_MS = 300
// How long an anticipated keyboard dismissal (focus left every keyboard
// input) stands before the keyboard's own state has to confirm it. Longer
// than any keyboard slide plus a viewport sample; a keyboard that is still up
// after this gets the foot back.
const KEYBOARD_DISMISSAL_ANTICIPATION_MS = 900
// The dock's snapshot when bottom_editor_dock is off.
const subscribeToNoDock = () => () => {}
const CONTROL_SWIPE_TARGET_ID = 'sidebar-control-swipe-target'
const isServerBackedViewableQuest = quest =>
    !!quest?.id &&
    quest.id !== '__PRIVATE__' &&
    !quest.is_draft &&
    !quest.localChildQuest &&
    !quest.isLocalChildQuest &&
    !quest.isLocalRootQuest &&
    !quest.localPublishErrorStatus

const isFilterReadyForPanelHistory = filter => {
    if (!filter?.questId) return true
    return !!cache.getCachedQuest(filter.questId)
}

const getPanelNavDetail = (swiper, onePanel, extra = {}) => {
    const activeIndex = Number.isInteger(swiper?.activeIndex) ? swiper.activeIndex : null
    const centerIndex =
        activeIndex == null
            ? getCenterIndex()
            : isMobile || onePanel
            ? activeIndex
            : activeIndex + 1
    const panel = useStore.getState().panels.state[centerIndex]
    return {
        activeIndex,
        centerIndex,
        panelId: panel?.panelId,
        filter: panel?.filter,
        isMobile,
        onePanel,
        ...extra,
    }
}

const getPanelNavControlDetail = (metricDetail, fallbackSource) => {
    const isSyntheticEvent = !!metricDetail?.nativeEvent
    const isDomEvent = !!metricDetail?.type && typeof metricDetail?.timeStamp === 'number'
    if (metricDetail && !isSyntheticEvent && !isDomEvent) return metricDetail

    return {
        source: fallbackSource,
        ...getPanelNavInputDetail(metricDetail),
    }
}

const isPointInsideRect = (rect, x, y) => {
    if (!rect) return false
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
}

const isElementInsidePanelSwipeExclusion = el => {
    if (!(typeof Element !== 'undefined' && el instanceof Element)) return false
    return !!el.closest?.(PANEL_SWIPE_EXCLUSION_SELECTOR)
}

const isEventInsidePanelSwipeExclusion = event => {
    try {
        const path =
            typeof event?.composedPath === 'function' ? event.composedPath() : event?.path || []
        if (path.some(isElementInsidePanelSwipeExclusion)) return true
        return isElementInsidePanelSwipeExclusion(event?.target)
    } catch (e) {
        return false
    }
}

const HTML5toTouch = {
    backends: [
        {
            backend: HTML5Backend,
            transition: MouseTransition,
        },
        {
            backend: TouchBackend,
            options: {
                // Keep ownership of the pointer on touch and avoid falling back to scroll
                enableTouchEvents: true,
                enableMouseEvents: false,
                delayTouchStart: 0,
                delayMouseStart: 0,
                useTouch: true,
                useMouse: false,
                useKeyboard: false,
                ignoreContextMenu: true,
                enableHoverOutsideTarget: true,
                touchSlop: 0,
            },
            preview: true,
            transition: TouchTransition,
        },
    ],
}

// Track swipe state outside component without ref-like objects to avoid freezes in prod
let prevDeltaXVal = 0
let isSwipingFlag = false
let isSwipingLeftFlag = false
let isSwipingRightFlag = false
// Manual slide flag shared across modules for transform animation coordination
let manualSlideFlag = false
export const setManualSlide = val => {
    manualSlideFlag = !!val
}
export const isManualSlideActive = () => manualSlideFlag
export const consumeManualSlideFlag = () => {
    const was = manualSlideFlag
    manualSlideFlag = false
    return was
}

const CONTROL_SWIPE_FALLBACK_RECT = { left: 0, top: 0, right: 200, bottom: 160 }
const CONTROL_SWIPE_EXPANSION = { x: 12, y: 12 }

function WalletBalanceBadge() {
    const openWalletModal = useStore(state => state.actions.modalActions.openWalletModal)

    return (
        <WithTooltip asChild tip="Open wallet.">
            <div className={cn(styles.walletBalanceBadge)} onClick={() => openWalletModal()}>
                <WalletBalanceAmount />
            </div>
        </WithTooltip>
    )
}

export default React.memo(function PanelController() {
    //console.log('RENDER PANEL CONTROLLER')
    const swiperRef = getSwiperRef()
    const controlContainerRef = useRef(null)
    const panelControllerRef = useRef(null)
    const panelNavSideEffectsRef = useRef(null)
    const latestPanelNavSideEffectIdRef = useRef(0)
    const [controlSwipeRect, setControlSwipeRect] = useState(null)
    if (!panelNavSideEffectsRef.current) {
        panelNavSideEffectsRef.current = createPanelNavSideEffectQueue({
            runBatch: effects => {
                const latestEffect = [...effects]
                    .reverse()
                    .find(effect => effect.id === latestPanelNavSideEffectIdRef.current)
                if (latestEffect && latestEffect.shouldPersistUserSpaceOptions !== false) {
                    try {
                        const updatePromise = api.updateUserSpaceOptions({
                            last_path: latestEffect.path,
                            filter: latestEffect.filter,
                        })
                        updatePromise?.catch?.(error => {
                            console.warn(
                                'Error updating panel navigation user space options',
                                error,
                            )
                        })
                    } catch (error) {
                        console.warn('Error updating panel navigation user space options', error)
                    }
                }

                let shouldInvalidateActions = false
                for (const effect of effects) {
                    if (effect.shouldTrackQuestView && effect.filter?.questId) {
                        shouldInvalidateActions = true
                        try {
                            api.userViewsQuest(effect.filter.questId)
                        } catch (error) {
                            console.warn('Error recording panel navigation quest view', error)
                        }
                    }
                }

                if (shouldInvalidateActions) {
                    void queryClient.invalidateQueries({
                        queryKey: ['actions'],
                        exact: true,
                    })
                }
            },
        })
    }

    const appearanceTheme = useStore(state => state.appearanceTheme)
    const showSidebar = useStore(state => state.showSidebar)
    const closeSidebarOnBlur = useStore(state => state.closeSidebarOnBlur)
    const closeSidebarOnSwipe = useStore(state => state.closeSidebarOnSwipe)
    const setShowSidebar = useStore.getState().setShowSidebar
    const setCloseSidebarOnBlur = useStore(state => state.setCloseSidebarOnBlur)

    const showRightSidebar = useStore(state => state.showRightSidebar)
    const setShowRightSidebar = useStore.getState().setShowRightSidebar
    const openAuthModal = useStore(state => state.actions.modalActions.openAuthModal)

    const [showFilterOptions, setShowFilterOptions] = useState(false)
    const [slideUpdates, triggerTouchSlideUpdate] = useState(0)
    const [viewportKeyboardOpen, setViewportKeyboardOpen] = useState(false)
    const [viewportKeyboardHeight, setViewportKeyboardHeight] = useState(0)
    const [nativeKeyboardState, setNativeKeyboardState] = useState(() =>
        readNativeKeyboardState(typeof window === 'undefined' ? undefined : window),
    )
    const [replyComposerKeyboardPending, setReplyComposerKeyboardPending] = useState(false)
    const keyboardOpenRef = useRef(false)
    // bottom_editor_dock's page controller while the feature is on
    // (lib/bottomEditorDock); read by the focus handlers below.
    const bottomEditorDockRef = useRef(null)
    const keyboardExpectedRef = useRef(false)
    const keyboardFocusFallbackTimerRef = useRef(null)
    const previousKeyboardOpenRef = useRef(false)
    const [isConsoleOpen, setIsConsoleOpen] = useState(false)
    const isAndroidMobileApp =
        typeof window !== 'undefined' && Boolean(window.KNOVIGATOR_IS_MOBILE && isAndroid)
    const isMobileAppOrAndroid =
        typeof window !== 'undefined'
            ? Boolean(window.KNOVIGATOR_IS_MOBILE) || isAndroid
            : isAndroid
    const isMobileIosBrowser =
        typeof window !== 'undefined' ? !window.KNOVIGATOR_IS_MOBILE && isIOS : false

    const numPanels = useStore(state => state.numPanels)
    const setNumPanels = useStore.getState().setNumPanels
    const onePanel = !isMobile && numPanels === 1
    const [isInitialized, setIsInitialized] = useState(false)
    const isModeSwitchingRef = useRef(false)
    // Track last known one-panel active index and center id
    const lastOnePanelActiveRef = useRef(null)
    const lastOnePanelCenterIdRef = useRef(null)
    const previousNumPanelsRef = useRef(null)
    const keyboardState = resolveKeyboardState({
        nativeKeyboardState,
        viewportKeyboardOpen,
        viewportKeyboardHeight,
    })
    const hasNativeKeyboardState = nativeKeyboardState !== null
    // What the keyboard's own state says, and what the foot acts on. The two
    // part for the length of a dismissal: the native bridge and the viewport
    // both report the keyboard gone only once its slide is over, but focus
    // leaving every keyboard input (iOS "Done", a tap on the thread) is the
    // dismissal's first sign, and the foot starts back on it — the navbar
    // rising and the pill gliding up as the keyboard slides away, rather than
    // a beat after it has gone. The keyboard's own state clears the
    // anticipation when it catches up; a keyboard that stays (focus returned
    // to a field, the state never following) gets the foot back.
    const sourceKeyboardOpen = keyboardState.visible
    const [keyboardDismissalAnticipated, setKeyboardDismissalAnticipated] = useState(false)
    const keyboardOpen = sourceKeyboardOpen && !keyboardDismissalAnticipated
    useEffect(() => {
        if (!sourceKeyboardOpen) setKeyboardDismissalAnticipated(false)
    }, [sourceKeyboardOpen])
    useEffect(() => {
        if (!keyboardDismissalAnticipated) return
        const timer = window.setTimeout(
            () => setKeyboardDismissalAnticipated(false),
            KEYBOARD_DISMISSAL_ANTICIPATION_MS,
        )
        return () => window.clearTimeout(timer)
    }, [keyboardDismissalAnticipated])
    const isMobileSurface =
        isMobile || (typeof window !== 'undefined' && Boolean(window.KNOVIGATOR_IS_MOBILE))
    // A keyboard is on its way: focus has landed in a keyboard input and the
    // keyboard's own state has not caught up (shouldExpectKeyboardOnFocus).
    // The foot lays out for it from here. Cleared by the keyboard arriving, by
    // focus leaving again, or by nothing arriving in the fallback window
    // (resolveKeyboardExpectationTimeoutMs: the Android shell gets no window).
    const [keyboardExpected, setKeyboardExpected] = useState(false)
    // The last keyboard reported, raw — this load or an earlier one
    // (keyboardHeightMemory): a keyboard seen is what lets the next one be
    // expected at focus (shouldExpectKeyboardOnFocus), and its height is where
    // the pill glides to ahead of the viewport (resolveKeyboardPendingShift).
    const lastKeyboardHeightRef = useRef(readRememberedKeyboardHeight())
    useEffect(() => {
        if (keyboardOpen) setKeyboardExpected(false)
    }, [keyboardOpen])
    useEffect(() => {
        keyboardExpectedRef.current = keyboardExpected
    }, [keyboardExpected])
    useEffect(() => {
        if (!keyboardExpected) return
        const timeoutMs = resolveKeyboardExpectationTimeoutMs({
            isAndroidMobileApp,
            fallbackMs: KEYBOARD_FOCUS_FALLBACK_MS,
        })
        if (timeoutMs == null) return
        const timer = window.setTimeout(() => setKeyboardExpected(false), timeoutMs)
        return () => window.clearTimeout(timer)
    }, [keyboardExpected, isAndroidMobileApp])
    // The iOS reserve nets WebKit's caret-reveal shift out of the native
    // keyboard height (resolvePanelKeyboardHeight). That shift is read at render
    // time, and nothing re-rendered when it moved afterwards — the reader
    // panning the page under the keyboard, WebKit revealing the caret again as
    // the composer grows — so the composer drifted off the keyboard's edge,
    // under it or above it. Re-render on visual viewport movement while the
    // native keyboard is up so the reserve follows.
    const [, bumpIosViewportSync] = useState(0)
    useEffect(() => {
        const visualViewport = window.visualViewport
        if (!hasNativeKeyboardState || !isIOS || !keyboardOpen || !visualViewport) return
        if (viewportResizesForKeyboard({ isAndroid, nativeCaps: window.KNOVIGATOR_NATIVE_CAPS })) {
            return
        }

        const sync = throttle(() => bumpIosViewportSync(tick => tick + 1), 60, {
            leading: false,
            trailing: true,
        })
        visualViewport.addEventListener('scroll', sync)
        visualViewport.addEventListener('resize', sync)
        return () => {
            sync.cancel()
            visualViewport.removeEventListener('scroll', sync)
            visualViewport.removeEventListener('resize', sync)
        }
    }, [hasNativeKeyboardState, keyboardOpen])
    const keyboardHeight = resolvePanelKeyboardHeight({
        keyboardState,
        hasNativeKeyboardState,
        isIOS,
        visualViewportOffsetTop: window.visualViewport?.offsetTop,
    })
    if (keyboardOpen && keyboardState.height > 0)
        lastKeyboardHeightRef.current = keyboardState.height
    useEffect(() => {
        if (keyboardOpen && keyboardState.height > 0) rememberKeyboardHeight(keyboardState.height)
    }, [keyboardOpen, keyboardState.height])
    // Where the layout viewport shrinks for the keyboard the foot rides its
    // edge; elsewhere WebKit pans the page and the foot reserves the keyboard.
    const viewportResizes = viewportResizesForKeyboard({
        isAndroid,
        nativeCaps: typeof window === 'undefined' ? undefined : window.KNOVIGATOR_NATIVE_CAPS,
    })
    // What the panel foot lays out for — the composer pill's place, the navbar
    // under it, the reserve — as against what the keyboard's state says. The
    // two part while a keyboard is expected and not yet reported: the foot is
    // open with no reserve, the pill at the panel's bottom edge, from where the
    // resized viewport (Android) or WebKit's caret pan (iOS) carries it onto
    // the keyboard's edge.
    const { open: footKeyboardOpen, height: footKeyboardHeight } = resolveFootKeyboardState({
        keyboardOpen,
        keyboardHeight,
        keyboardExpected,
    })
    const bottomNavigationHidden = shouldHideBottomNavigation({
        isMobile: isMobileSurface,
        keyboardOpen,
        replyComposerKeyboardPending,
    })
    // The keyboard has just gone and the foot is on its way back: the navbar
    // returning and the pill's foot both move over this window rather than
    // cutting, so they arrive as one piece. Derived in render (the React
    // "adjust state on a prop change" form) so the settling render is the
    // very one in which the navbar mounts and the spacer changes.
    const [keyboardSettling, setKeyboardSettling] = useState(false)
    const [keyboardOpenLastSeen, setKeyboardOpenLastSeen] = useState(footKeyboardOpen)
    if (keyboardOpenLastSeen !== footKeyboardOpen) {
        setKeyboardOpenLastSeen(footKeyboardOpen)
        if (!footKeyboardOpen && isMobileSurface) setKeyboardSettling(true)
    }
    useEffect(() => {
        if (!keyboardSettling) return
        const timer = window.setTimeout(() => setKeyboardSettling(false), KEYBOARD_SETTLE_MS)
        return () => window.clearTimeout(timer)
    }, [keyboardSettling])
    // What the keyboard is doing, for the composer pill's glide at the foot
    // (lib/bottomEditorGlide).
    const keyboardPhase = resolveKeyboardPhase({
        // Use the reported/anticipated keyboard state here, not the derived
        // foot state: resolveFootKeyboardState opens the foot while a keyboard
        // is merely expected, which would otherwise make the `expecting`
        // phase unreachable and skip the remembered-height pre-lift.
        keyboardOpen,
        keyboardExpected,
        keyboardLeaving: keyboardDismissalAnticipated || keyboardSettling,
    })
    const centerPanelId = useStore(state => {
        const ix = state.centerIndex ?? getCenterIndex()
        return state.panels.state[ix]?.panelId
    })
    const centerPanelIsThread = useStore(state => {
        const ix = state.centerIndex ?? getCenterIndex()
        return Boolean(state.panels.state[ix]?.filter?.questId)
    })
    // fixed_header: the panel's place bar owns back navigation, the wallet
    // balance moves into the Wallet menu item, and on phones a thread gives the
    // corners (sidebar button, avatar) to the bar's breadcrumb.
    const fixedHeader = fixedHeaderEnabled()
    const hideCornersForThread = fixedHeader && isMobile && centerPanelIsThread
    const [bottomEditorPanelIds, setBottomEditorPanelIds] = useState(() => new Set())
    const handleBottomEditorPresenceChange = useCallback((panelId, mounted) => {
        if (panelId == null) return
        setBottomEditorPanelIds(current => {
            if (current.has(panelId) === mounted) return current
            const next = new Set(current)
            if (mounted) next.add(panelId)
            else next.delete(panelId)
            return next
        })
    }, [])
    // bottom_editor_dock: a thread's phone pill takes its foot from one measured
    // quantity (lib/bottomEditorDock) instead of the prediction above. The dock
    // positions the pill and covers the navbar itself, so under it the navbar
    // stays mounted through the keyboard; React sees only its coarse snapshot.
    const dockEnabled = isMobileSurface && bottomEditorDockEnabled()
    const bottomEditorDock = dockEnabled ? getBottomEditorDock({ isAndroid }) : null
    bottomEditorDockRef.current = bottomEditorDock
    const dockSnapshot = useSyncExternalStore(
        bottomEditorDock ? bottomEditorDock.subscribe : subscribeToNoDock,
        bottomEditorDock ? bottomEditorDock.getSnapshot : inactiveBottomEditorDockSnapshot,
    )
    const centerDocked = Boolean(bottomEditorDock) && bottomEditorPanelIds.has(centerPanelId)
    useEffect(() => {
        if (!bottomEditorDock) return
        bottomEditorDock.start()
        return () => bottomEditorDock.stop()
    }, [bottomEditorDock])
    // keyboard_tracking: where the shell offers its measured
    // keyboard edge (treechat-mobile modules/keyboard-edge), the dock takes the
    // pill's place from it (lib/keyboardEdgeAdapter). Asked for only while a
    // docked thread is centred: under the edge the shell stops resizing the
    // WebView, which only the docked pill makes up for. Elsewhere the edge is
    // handed back, and the shell resizes again from its next closed keyboard.
    const keyboardEdgeTracker =
        centerDocked &&
        keyboardEdgeTrackerEnabled() &&
        shellOffersKeyboardEdge(window.KNOVIGATOR_NATIVE_CAPS)
    useEffect(() => {
        if (!keyboardEdgeTracker) return
        const edgeAdapter = getKeyboardEdgeAdapter()
        edgeAdapter.start()
        return () => edgeAdapter.stop()
    }, [keyboardEdgeTracker])
    useEffect(() => {
        bottomEditorDock?.setActivePanel(centerDocked ? centerPanelId : null)
    }, [bottomEditorDock, centerDocked, centerPanelId])
    // A mounted pill, rather than the feature flag alone, owns the panel foot.
    // System threads without a reply surface keep the New button and navbar.
    // A phone keeps its navbar under the pill either way; see the helper.
    const { newButtonHidden, navigationHidden: navigationHiddenUnderEditor } =
        resolveBottomControlsUnderEditor({
            isMobile: isMobileSurface,
            bottomNavigationHidden,
            keyboardOpen: centerDocked ? false : footKeyboardOpen,
            bottomEditorMounted: bottomEditorPanelIds.has(centerPanelId),
        })
    // hide_thread_navbar: a phone's thread panels go without the navbar
    // altogether, the pill taking the foot on its own (lib/threadNavigation).
    const navigationHidden =
        navigationHiddenUnderEditor ||
        shouldHideThreadNavigation({
            enabled: hideThreadNavbarEnabled(),
            isMobile: isMobileSurface,
            isThreadPanel: centerPanelIsThread,
            bottomEditorMounted: bottomEditorPanelIds.has(centerPanelId),
        })

    useEffect(() => {
        const updateFromSnapshot = event => {
            const nextState = normalizeNativeKeyboardState(event?.detail)
            if (!nextState) return

            setNativeKeyboardState(currentState =>
                reconcileNativeKeyboardState({
                    currentState,
                    nextState,
                    ignoreHeightChanges: isAndroidMobileApp,
                }),
            )
            // The shell rolling back a replay no keyboard followed (a hidden
            // report while one is expected): nothing is coming.
            if (
                shouldEndKeyboardExpectationOnNativeReport({
                    keyboardExpected: keyboardExpectedRef.current,
                    visible: nextState.visible,
                })
            ) {
                setKeyboardExpected(false)
            }
            if (keyboardFocusFallbackTimerRef.current != null) {
                window.clearTimeout(keyboardFocusFallbackTimerRef.current)
                keyboardFocusFallbackTimerRef.current = null
            }
            setReplyComposerKeyboardPending(false)
        }
        const updateFromCapabilities = () => {
            setNativeKeyboardState(currentState =>
                reconcileNativeKeyboardState({
                    currentState,
                    nextState: readNativeKeyboardState(window),
                    ignoreHeightChanges: isAndroidMobileApp,
                }),
            )
        }

        window.addEventListener(NATIVE_KEYBOARD_CHANGE_EVENT, updateFromSnapshot)
        window.addEventListener('nativeVersionInfoChange', updateFromCapabilities)
        return () => {
            window.removeEventListener(NATIVE_KEYBOARD_CHANGE_EVENT, updateFromSnapshot)
            window.removeEventListener('nativeVersionInfoChange', updateFromCapabilities)
        }
    }, [isAndroidMobileApp])

    useEffect(() => {
        keyboardOpenRef.current = keyboardOpen
        if (!keyboardOpen) return

        if (keyboardFocusFallbackTimerRef.current != null) {
            window.clearTimeout(keyboardFocusFallbackTimerRef.current)
            keyboardFocusFallbackTimerRef.current = null
        }
        setReplyComposerKeyboardPending(false)
    }, [keyboardOpen])

    useEffect(() => {
        if (!isMobileSurface) return

        let focusFrame = 0
        const clearKeyboardFocusFallback = () => {
            if (keyboardFocusFallbackTimerRef.current != null) {
                window.clearTimeout(keyboardFocusFallbackTimerRef.current)
                keyboardFocusFallbackTimerRef.current = null
            }
            setReplyComposerKeyboardPending(false)
        }
        const armKeyboardFocusFallback = target => {
            if (!isReplyComposerFocusTarget(target) || keyboardOpenRef.current) return

            if (keyboardFocusFallbackTimerRef.current != null) {
                window.clearTimeout(keyboardFocusFallbackTimerRef.current)
            }
            setReplyComposerKeyboardPending(true)
            keyboardFocusFallbackTimerRef.current = window.setTimeout(() => {
                keyboardFocusFallbackTimerRef.current = null
                const retainFallback = shouldRetainReplyComposerKeyboardFallback({
                    isAndroidMobileApp,
                    hasNativeKeyboardState: window.KNOVIGATOR_NATIVE_CAPS?.keyboardState === true,
                    replyComposerFocused: isReplyComposerFocusTarget(document.activeElement),
                })
                if (!keyboardOpenRef.current && !retainFallback) {
                    setReplyComposerKeyboardPending(false)
                }
            }, KEYBOARD_FOCUS_FALLBACK_MS)
        }
        const cancelFocusFrame = () => {
            if (!focusFrame) return
            cancelAnimationFrame(focusFrame)
            focusFrame = 0
        }
        let dismissalFrame = 0
        const cancelDismissalFrame = () => {
            if (!dismissalFrame) return
            cancelAnimationFrame(dismissalFrame)
            dismissalFrame = 0
        }
        const handleFocusIn = event => {
            cancelFocusFrame()
            cancelDismissalFrame()
            const targetIsKeyboardInput = isKeyboardInputElement(event.target)
            // Focus back in a field keeps the keyboard, or brings it back.
            if (targetIsKeyboardInput) setKeyboardDismissalAnticipated(false)
            const dock = bottomEditorDockRef.current
            // Focus back in a field ends any dismissal the dock was running.
            if (dock && targetIsKeyboardInput) dock.setDismissing(false)
            if (dock?.getSnapshot().docked) {
                // Under a docked thread the pill moves on the geometry the
                // keyboard actually produces. The Android shell still resizes
                // for the coming keyboard at once, from the last one it saw —
                // that resize is geometry the dock then measures.
                // With the shell's measured edge nothing is resized for the
                // keyboard at all, so nothing is asked for or expected.
                if (
                    targetIsKeyboardInput &&
                    isAndroid &&
                    window.KNOVIGATOR_NATIVE_CAPS?.keyboardOpening === true &&
                    !keyboardEdgeActive() &&
                    !keyboardOpenRef.current &&
                    !dock.getSnapshot().keyboardPresent
                ) {
                    postMessageToNative(buildKeyboardOpeningMessage())
                }
                // iOS resizes a few frames after WebKit has judged the pill
                // covered and panned the whole page to it: the pill goes where
                // the last keyboard put it first (lib/bottomEditorDock).
                if (
                    targetIsKeyboardInput &&
                    !isAndroid &&
                    !keyboardEdgeActive() &&
                    !keyboardOpenRef.current &&
                    event.target?.closest?.('[data-bottom-editor-pill="true"]')
                ) {
                    dock.expectKeyboard(lastKeyboardHeightRef.current)
                }
                // Under the shell's measured edge the pill goes where the coming
                // keyboard will land at once: the page opening the editor paints
                // none of the edge's samples while the keyboard rises over it.
                if (
                    targetIsKeyboardInput &&
                    keyboardEdgeActive() &&
                    !keyboardOpenRef.current &&
                    event.target?.closest?.('[data-bottom-editor-pill="true"]')
                ) {
                    dock.expectKeyboard(
                        resolveExpectedEdgeCover({
                            remembered: rememberedKeyboardEdgeCover(),
                            legacyHeight: lastKeyboardHeightRef.current,
                            isAndroid,
                            systemControlsBottom: parseFloat(
                                document.documentElement.style.getPropertyValue(
                                    '--native-system-controls-bottom',
                                ),
                            ),
                            viewportHeight: window.innerHeight,
                        }),
                    )
                }
            } else if (
                shouldExpectKeyboardOnFocus({
                    isMobile: true,
                    keyboardOpen: keyboardOpenRef.current,
                    targetIsKeyboardInput,
                    lastKeyboardHeight: lastKeyboardHeightRef.current,
                    canPredictKeyboardOpening: canPredictKeyboardOpening({
                        isAndroidMobileApp,
                        keyboardOpeningCapability:
                            window.KNOVIGATOR_NATIVE_CAPS?.keyboardOpening === true,
                    }),
                })
            ) {
                setKeyboardExpected(true)
                // The shell can shrink the WebView for the coming keyboard now,
                // by the last keyboard it saw, instead of a layout pass after
                // the keyboard has arrived (treechat-mobile nativeKeyboardBridge).
                if (window.KNOVIGATOR_NATIVE_CAPS?.keyboardOpening === true) {
                    postMessageToNative(buildKeyboardOpeningMessage())
                }
            }
            armKeyboardFocusFallback(event.target)
        }
        const handlePointerDown = event => {
            armKeyboardFocusFallback(event.target)
        }
        const handleFocusOut = event => {
            // Where focus lands is known a frame later, once any focusin has run.
            // A keyboard the dock expects at the focus has placed the pill
            // already: focus leaving before it comes returns it too.
            const dockKeyboardPresent = Boolean(
                bottomEditorDockRef.current?.getSnapshot().keyboardPresent ||
                    bottomEditorDockRef.current?.expectingKeyboard(),
            )
            if (keyboardOpenRef.current || keyboardExpectedRef.current || dockKeyboardPresent) {
                cancelDismissalFrame()
                dismissalFrame = requestAnimationFrame(() => {
                    dismissalFrame = 0
                    if (!isKeyboardInputElement(document.activeElement)) {
                        setKeyboardExpected(false)
                        // The dismissal's start: the dock folds and returns to
                        // its rail now where that moves it (lib/bottomEditorDock).
                        bottomEditorDockRef.current?.setDismissing(true)
                    }
                    const anticipation = resolveKeyboardDismissalAnticipation({
                        keyboardOpen:
                            keyboardOpenRef.current ||
                            Boolean(bottomEditorDockRef.current?.getSnapshot().keyboardPresent),
                        activeElementIsKeyboardInput: isKeyboardInputElement(
                            document.activeElement,
                        ),
                        shellAnswersDismissal:
                            isAndroid &&
                            window.KNOVIGATOR_NATIVE_CAPS?.keyboardState === true &&
                            !keyboardEdgeActive(),
                    })
                    // Under a tracked dock the dock runs the dismissal; the
                    // legacy foot's anticipation would only re-render this
                    // controller while the keyboard slides.
                    if (anticipation === 'local') {
                        if (!keyboardEdgeActive()) {
                            setKeyboardDismissalAnticipated(true)
                        }
                    } else if (anticipation === 'shell') {
                        // The shell answers with nativeKeyboardChange once it
                        // has regrown the WebView; the foot moves on that.
                        postMessageToNative(buildKeyboardDismissingMessage())
                    }
                })
            }
            if (!isReplyComposerFocusTarget(event.target)) return
            cancelFocusFrame()
            focusFrame = requestAnimationFrame(() => {
                focusFrame = 0
                if (!isReplyComposerFocusTarget(document.activeElement)) {
                    clearKeyboardFocusFallback()
                }
            })
        }

        document.addEventListener('focusin', handleFocusIn)
        document.addEventListener('focusout', handleFocusOut)
        document.addEventListener('pointerdown', handlePointerDown)

        return () => {
            cancelFocusFrame()
            cancelDismissalFrame()
            if (keyboardFocusFallbackTimerRef.current != null) {
                window.clearTimeout(keyboardFocusFallbackTimerRef.current)
                keyboardFocusFallbackTimerRef.current = null
            }
            document.removeEventListener('focusin', handleFocusIn)
            document.removeEventListener('focusout', handleFocusOut)
            document.removeEventListener('pointerdown', handlePointerDown)
        }
    }, [])
    const measureControlSwipeRect = useCallback(() => {
        const el = controlContainerRef.current
        if (!el) {
            setControlSwipeRect(null)
            return
        }
        const rect = el.getBoundingClientRect()
        const expandedRect = {
            left: rect.left - CONTROL_SWIPE_EXPANSION.x,
            top: rect.top - CONTROL_SWIPE_EXPANSION.y,
            right: rect.right + CONTROL_SWIPE_EXPANSION.x,
            bottom: rect.bottom + CONTROL_SWIPE_EXPANSION.y,
        }
        setControlSwipeRect(prev => {
            if (
                prev &&
                prev.left === expandedRect.left &&
                prev.top === expandedRect.top &&
                prev.right === expandedRect.right &&
                prev.bottom === expandedRect.bottom
            ) {
                return prev
            }
            return expandedRect
        })
    }, [])

    // Ensure mode switching flag is false on mount
    useEffect(() => {
        isModeSwitchingRef.current = false
    }, [])

    useEffect(() => {
        return () => {
            panelNavSideEffectsRef.current?.cancel()
        }
    }, [])

    useEffect(() => {
        let outerFrame = 0
        let innerFrame = 0

        markStartupMetric('panel-controller-mounted', {
            hasCurrentUser: Boolean(gon?.currentUser),
            isMobile: Boolean(isMobile),
        })

        outerFrame = requestAnimationFrame(() => {
            innerFrame = requestAnimationFrame(() => {
                markStartupMetric('panel-controller-interactive', {
                    hasSidebarToggle: Boolean(
                        document.querySelector('[data-testid="sidebar-toggle-button"]'),
                    ),
                })
            })
        })

        return () => {
            cancelAnimationFrame(outerFrame)
            cancelAnimationFrame(innerFrame)
        }
    }, [])

    useLayoutEffect(() => {
        measureControlSwipeRect()
        window.addEventListener('resize', measureControlSwipeRect)
        return () => {
            window.removeEventListener('resize', measureControlSwipeRect)
        }
    }, [measureControlSwipeRect])

    useEffect(() => {
        const raf = requestAnimationFrame(measureControlSwipeRect)
        return () => cancelAnimationFrame(raf)
    }, [measureControlSwipeRect, newButtonHidden, navigationHidden, isConsoleOpen, onePanel, showSidebar, isMobileAppOrAndroid])

    const canSlidePrev = useCallback(() => {
        const num = useStore.getState().numPanels
        if (isMobile || num === 1) {
            // Use store centerIndex for reactivity instead of swiperRef.isBeginning
            const centerIx = useStore.getState().centerIndex
            return centerIx > 0
        }
        // 3-panel mode: allow prev only if the panel to the left of center is non-empty
        const panels = useStore.getState().panels.state
        const centerIx = getCenterIndex()
        const left = panels[centerIx - 1]
        return Boolean(left && !left.empty)
    }, [])

    const canSlideNext = useCallback(() => {
        const num = useStore.getState().numPanels
        if (isMobile || num === 1) {
            // Use store centerIndex for reactivity instead of swiperRef.isEnd
            const panels = useStore.getState().panels.state
            const centerIx = useStore.getState().centerIndex
            return centerIx < panels.length - 1
        }
        // 3-panel mode: allow next only if the panel to the right of center is non-empty
        const panels = useStore.getState().panels.state
        const centerIx = getCenterIndex()
        const right = panels[centerIx + 1]
        return Boolean(right && !right.empty)
    }, [])

    const toggleNumPanels = useCallback(() => {
        // Toggle between 1 and 3 panels
        const newNumPanels = numPanels === 1 ? 3 : 1
        // Guard: mark that a mode switch is in progress before state flips,
        // so slide-change events during reconfig are ignored
        isModeSwitchingRef.current = true
        setNumPanels(newNumPanels)

        // Save the preference to user space options
        if (window.gon?.currentUser) {
            api.updateUserSpaceOptions({ numPanels: newNumPanels })
        }

        // The useEffect for numPanels change will handle the state synchronization
    }, [numPanels, setNumPanels])

    // ensure the wallet details are loaded when app starts
    useWalletDetails(BsvWalletType.SHUALLET)

    // Initialize numPanels from user space options when component mounts
    useEffect(() => {
        const userPanelPreference = window.gon?.currentUser?.space_options?.numPanels
        if (userPanelPreference !== undefined) {
            setNumPanels(userPanelPreference)
        } else if (!window.gon?.currentUser) {
            setNumPanels(1)
        }
        setIsInitialized(true)
    }, [setNumPanels])

    // Handle swiper update when numPanels changes
    useEffect(() => {
        if (swiperRef.current && isInitialized) {
            // Determine if this is a mode switch or initial load
            const isInitialLoad = previousNumPanelsRef.current === null
            const isModeSwitch =
                previousNumPanelsRef.current !== null && previousNumPanelsRef.current !== numPanels

            // Skip if nothing changed
            if (!isInitialLoad && !isModeSwitch) return

            // Only handle actual mode switches here. For initial load, do not modify buffers
            // or slide — just record the current mode and exit to avoid index drift.
            if (isInitialLoad) {
                // Ensure Swiper computes layout on first paint
                requestAnimationFrame(() => {
                    const sw = swiperRef.current
                    if (sw) {
                        try {
                            // Keep slidesPerView consistent with current mode
                            sw.params.slidesPerView = isMobile || onePanel ? 1 : 3
                            sw.update()
                            sw.updateSlides()
                            sw.updateProgress()
                            sw.updateSlidesClasses()
                        } catch (e) {
                            console.warn('INITIAL LOAD - swiper update error', e)
                        }
                    }
                    previousNumPanelsRef.current = numPanels
                    isModeSwitchingRef.current = false
                })
                return
            }

            // Set flag to prevent unwanted slide changes during mode switch
            isModeSwitchingRef.current = true
            console.log('MODE SWITCH STARTING')

            // Capture BEFORE any reconfig/RAF to avoid swiper resetting activeIndex
            const swiperAtStart = swiperRef.current
            const capturedActiveIndex = swiperAtStart?.activeIndex ?? 0
            const panelsBefore = useStore.getState().panels.state
            const actualPrevMode = previousNumPanelsRef.current || numPanels
            const savedCenterId =
                !isMobile && actualPrevMode === 1 ? lastOnePanelCenterIdRef.current : null
            const prevModeCenterIndex =
                isMobile || actualPrevMode === 1 ? capturedActiveIndex : capturedActiveIndex + 1
            const derivedCenterId = panelsBefore[prevModeCenterIndex]?.panelId
            const prevCenterPanelId = savedCenterId || derivedCenterId

            console.log('CAPTURED BEFORE MODE SWITCH:', {
                capturedActiveIndex,
                prevModeCenterIndex,
                prevCenterPanelId,
                savedCenterId,
                derivedCenterId,
                actualPrevMode,
                numPanelsNew: numPanels,
                panelsBefore: panelsBefore.map((p, i) => ({ i, id: p.panelId, empty: p.empty })),
            })

            // Give swiper time to reconfigure before updating
            requestAnimationFrame(() => {
                const swiper = swiperRef.current
                if (!swiper) return

                // Modify buffers based on NEW mode
                if (!isMobile && numPanels === 1) {
                    // When switching to one-panel desktop, remove bookend empty panels (same as mobile)
                    const panelsApi = useStore.getState().panels
                    let list = panelsApi.state
                    // Remove leading empty panels
                    while (list.length > 0 && list[0]?.empty) {
                        panelsApi.removePanelAtIndexNoSlide(0)
                        list = useStore.getState().panels.state
                    }
                    // Remove trailing empty panels
                    while (list.length > 0 && list[list.length - 1]?.empty) {
                        panelsApi.removePanelAtIndexNoSlide(list.length - 1)
                        list = useStore.getState().panels.state
                    }
                } else if (!isMobile && numPanels !== 1) {
                    // When switching to 3-panel desktop, ensure bookend empty panels exist
                    const panelsApi = useStore.getState().panels
                    let list = panelsApi.state
                    if (!list[0]?.empty) panelsApi.prependEmptyPanel()
                    list = useStore.getState().panels.state
                    if (!list[list.length - 1]?.empty) panelsApi.appendEmptyPanel()
                }

                // Get updated state after buffer adjustments
                const panels = useStore.getState().panels.state

                // When switching from 3-panel to 1-panel, we want to keep the center panel as active
                // When switching from 1-panel to 3-panel, we want to keep the active panel as center

                console.log('BEFORE MODE SWITCH (post-buffers):', {
                    actualPrevMode,
                    newNumPanels: onePanel ? 1 : 3,
                    capturedActiveIndex,
                    prevModeCenterIndex,
                    prevCenterPanelId,
                    storeActiveIndex: useStore.getState().activeIndex,
                    storeCenterIndex: useStore.getState().centerIndex,
                    currentCenterPanelIndex: panels.findIndex(p => p.panelId === prevCenterPanelId),
                    panelsLength: panels.length,
                    panels: panels.map((p, i) => ({
                        index: i,
                        id: p.panelId,
                        empty: p.empty,
                        isCenter: p.panelId === prevCenterPanelId,
                    })),
                })

                // Guard: in 3-up, wait until bookend empties exist before mapping indices
                if (!isMobile && numPanels !== 1) {
                    const haveBookends =
                        panels?.length >= 3 && panels[0]?.empty && panels[panels.length - 1]?.empty
                    if (!haveBookends) {
                        let tries = 0
                        const max = 20
                        const poll = () => {
                            const list = useStore.getState().panels.state
                            const ready =
                                list?.length >= 3 && list[0]?.empty && list[list.length - 1]?.empty
                            if (ready) {
                                let targetIndex = list.findIndex(
                                    p => p.panelId === prevCenterPanelId && !p.empty,
                                )
                                if (targetIndex === -1) targetIndex = list.findIndex(p => !p.empty)
                                if (targetIndex === -1) return
                                const slideToIndex = Math.max(0, targetIndex - 1)
                                try {
                                    swiper.update && swiper.update()
                                    swiper.updateSlides && swiper.updateSlides()
                                    swiper.updateSlidesClasses && swiper.updateSlidesClasses()
                                    swiper.updateProgress && swiper.updateProgress()
                                    swiper.virtual &&
                                        swiper.virtual.update &&
                                        swiper.virtual.update(true)
                                } catch {}
                                requestAnimationFrame(() => {
                                    try {
                                        swiper.slideTo(slideToIndex, 0, false)
                                    } catch {}
                                    requestAnimationFrame(() => {
                                        const sw = swiperRef.current
                                        const pNow = useStore.getState().panels.state
                                        if (!sw || !pNow?.length) return
                                        const activeIxNow = sw.activeIndex
                                        const centerIxNow = activeIxNow + 1
                                        const actualCenterId = pNow[centerIxNow]?.panelId
                                        if (
                                            actualCenterId &&
                                            prevCenterPanelId &&
                                            actualCenterId !== prevCenterPanelId
                                        ) {
                                            const fbIx = pNow.findIndex(
                                                p => p.panelId === prevCenterPanelId,
                                            )
                                            if (fbIx !== -1) {
                                                const fbSlide = Math.max(0, fbIx - 1)
                                                try {
                                                    sw.slideTo(fbSlide, 0, false)
                                                } catch {}
                                            }
                                        }
                                        // Sync store indices
                                        const actualActiveIndex = sw.activeIndex
                                        const cIx = actualActiveIndex + 1
                                        updateCenteredPanelId(cIx)
                                        useStore.getState().set({
                                            activeIndex: actualActiveIndex,
                                            centerIndex: cIx,
                                        })
                                        previousNumPanelsRef.current = numPanels
                                        isModeSwitchingRef.current = false
                                    })
                                })
                                return
                            }
                            if (tries++ < max) return requestAnimationFrame(poll)
                        }
                        requestAnimationFrame(poll)
                        return
                    }
                }

                // We no longer force slidesPerView here; Swiper prop will update
                // via render once bookend empties are present to avoid flicker

                // Find the panel to center after mode change
                let targetIndex = panels.findIndex(p => p.panelId === prevCenterPanelId && !p.empty)
                if (targetIndex === -1) {
                    // If current center panel is empty, find first non-empty panel
                    targetIndex = panels.findIndex(p => !p.empty)
                }

                console.log('MODE SWITCH - Target panel:', {
                    currentCenterPanelId: prevCenterPanelId,
                    targetIndex,
                    targetPanel: panels[targetIndex],
                })

                if (targetIndex !== -1) {
                    // Calculate the slide index based on NEW mode
                    // When switching TO 1-panel or mobile: activeIndex should be the center panel index
                    // When switching TO 3-panel desktop: activeIndex should be centerIndex - 1
                    const slideToIndex =
                        isMobile || onePanel ? targetIndex : Math.max(0, targetIndex - 1)

                    console.log('TARGET CALCULATION:', {
                        targetIndex,
                        slideToIndex,
                        targetPanelId: panels[targetIndex]?.panelId,
                        mode: isMobile || onePanel ? '1-panel/mobile' : '3-panel',
                        explanation:
                            isMobile || onePanel
                                ? `1-panel/mobile: slide to index ${slideToIndex} (same as target)`
                                : `3-panel desktop: slide to index ${slideToIndex} (target ${targetIndex} - 1)`,
                    })

                    // Update virtual slides and slide on next frame to avoid race with reconfig
                    requestAnimationFrame(() => {
                        try {
                            swiper.virtual && swiper.virtual.update && swiper.virtual.update(true)
                        } catch (e) {
                            console.log('MODE SWITCH - virtual.update error', e)
                        }
                        // Slide directly to the correct swiper index for the new mode
                        console.log('MODE SWITCH - Sliding to slideToIndex', {
                            slideToIndex,
                            targetIndex,
                            targetPanelId: panels[targetIndex]?.panelId,
                        })
                        try {
                            swiper.slideTo(slideToIndex, 0, false)
                        } catch (e) {
                            console.warn('MODE SWITCH - direct slideTo error', e)
                        }

                        // Verify the centered panel matches expectation; correct if off-by-one
                        requestAnimationFrame(() => {
                            const sw = swiperRef.current
                            const pNow = useStore.getState().panels.state
                            if (!sw || !pNow?.length) return
                            const isOne = useStore.getState().numPanels === 1 || isMobile
                            const activeIxNow = sw.activeIndex
                            const centerIxNow = isOne ? activeIxNow : activeIxNow + 1
                            const actualCenterId = pNow[centerIxNow]?.panelId
                            console.log('MODE SWITCH - VERIFY CENTER', {
                                activeIxNow,
                                centerIxNow,
                                actualCenterId,
                                expectedCenterId: prevCenterPanelId,
                                expectedActive: slideToIndex,
                            })
                            if (
                                actualCenterId &&
                                prevCenterPanelId &&
                                actualCenterId !== prevCenterPanelId
                            ) {
                                const fallbackIx = pNow.findIndex(
                                    p => p.panelId === prevCenterPanelId,
                                )
                                console.log('MODE SWITCH - CENTER MISMATCH, correcting', {
                                    fallbackIx,
                                    fallbackPanelId: prevCenterPanelId,
                                })
                                if (fallbackIx !== -1) {
                                    // Fallback: compute swiper index and slide directly
                                    const fbSlideIndex =
                                        useStore.getState().numPanels === 1 || isMobile
                                            ? fallbackIx
                                            : Math.max(0, fallbackIx - 1)
                                    try {
                                        sw.slideTo(fbSlideIndex, 0, false)
                                    } catch {}
                                }
                            }
                        })
                    })

                    console.log('AFTER slideTo:', {
                        swiperActiveIndex: swiper.activeIndex,
                        swiperRealIndex: swiper.realIndex,
                        visibleSlides: swiper.slides.length,
                    })

                    // Use requestAnimationFrame to ensure DOM has updated
                    requestAnimationFrame(() => {
                        const actualActiveIndex = swiper.activeIndex
                        const centerIndex = onePanel ? actualActiveIndex : actualActiveIndex + 1

                        console.log('AFTER MODE SWITCH:', {
                            actualActiveIndex,
                            centerIndex,
                            centerPanelId: panels[centerIndex]?.panelId,
                            lastActiveIndexRef: lastActiveIndexRef.current,
                        })

                        // Update store state
                        updateCenteredPanelId(centerIndex)
                        useStore.getState().set({ activeIndex: actualActiveIndex, centerIndex })
                        lastActiveIndexRef.current = actualActiveIndex

                        // Apply transforms based on mode
                        if (!onePanel && !isMobile) {
                            console.log('TRIGGERING TRANSFORMS for 3-panel mode')
                            // Call handlePanelTransforms with null as lastActiveIndex to trigger initial setup
                            handlePanelTransforms(actualActiveIndex, null, false)
                        } else if (onePanel && !isMobile) {
                            // Clear all transforms in 1-panel mode
                            console.log('CLEARING TRANSFORMS for 1-panel mode')
                            panels.forEach(panel => {
                                const el = panel.getTransformContainer?.()
                                if (el && el instanceof HTMLElement) {
                                    el.style.transform = ''
                                }
                            })
                        }

                        // Clear the mode switching flag after a delay
                        // Update the ref
                        previousNumPanelsRef.current = numPanels

                        // Clear the mode switching flag after a short delay
                        if (isModeSwitchingRef.current) {
                            // Use a simple timeout approach - Swiper's internal events should be done by then
                            setTimeout(() => {
                                isModeSwitchingRef.current = false

                                // Force a proper transform update now that mode switch is complete
                                const currentSwiper = swiperRef.current
                                if (currentSwiper && !onePanel && !isMobile) {
                                    const currentActiveIndex = currentSwiper.activeIndex

                                    handlePanelTransforms(currentActiveIndex, null, false)
                                    lastActiveIndexRef.current = currentActiveIndex
                                }
                            }, 300)
                        }
                    })
                }
            })
        }
    }, [numPanels, isInitialized, onePanel])

    const slidePrev = useCallback(
        (metricDetail = {}) => {
            const panels = useStore.getState().panels.state
            if (!hasPanelInDirection(panels, getCenterIndex(), 'prev')) {
                if (!isMobile) playPanelBoundaryFeedback(swiperRef.current, 'prev')
                return
            }

            const detail = getPanelNavControlDetail(metricDetail, 'header-arrow')
            startPanelNavMetric(
                detail.source || 'slide-prev',
                getPanelNavDetail(swiperRef.current, onePanel, {
                    direction: 'prev',
                    ...detail,
                }),
            )
            setManualSlide(true)
            slideTo(getCenterIndex() - 1)
        },
        [onePanel],
    )

    const slideNext = useCallback(
        (metricDetail = {}) => {
            const panels = useStore.getState().panels.state
            if (!hasPanelInDirection(panels, getCenterIndex(), 'next')) {
                if (!isMobile) playPanelBoundaryFeedback(swiperRef.current, 'next')
                return
            }

            const detail = getPanelNavControlDetail(metricDetail, 'header-arrow')
            startPanelNavMetric(
                detail.source || 'slide-next',
                getPanelNavDetail(swiperRef.current, onePanel, {
                    direction: 'next',
                    ...detail,
                }),
            )
            setManualSlide(true)
            slideTo(getCenterIndex() + 1)
        },
        [onePanel],
    )

    const handleSidebarClose = useCallback(() => {
        // We check the global state bc the sidebar state is not updated immediately when the sidebar is closed due to throttle.
        if (useStore.getState().showSidebar) {
            setShowSidebar(false)
        }
    }, [])

    const handleSidebarOpen = useCallback(() => {
        if (!useStore.getState().showSidebar) {
            setShowSidebar(true)
        }
    }, [])

    const isPointInsideControlTarget = useCallback(
        (x, y) => {
            if (typeof x !== 'number' || typeof y !== 'number') return false
            const rect = controlSwipeRect ?? CONTROL_SWIPE_FALLBACK_RECT
            return isPointInsideRect(rect, x, y)
        },
        [controlSwipeRect],
    )

    const isEventOverControlTarget = useCallback(
        event => {
            if (!event) return false
            if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
                if (isPointInsideControlTarget(event.clientX, event.clientY)) return true
            }
            const path =
                typeof event.composedPath === 'function' ? event.composedPath() : event.path || []
            return path.some(el => {
                if (!(typeof HTMLElement !== 'undefined' && el instanceof HTMLElement)) return false
                if (el.dataset?.swipeRegion === 'sidebar-control') return true
                return el.id === CONTROL_SWIPE_TARGET_ID
            })
        },
        [isPointInsideControlTarget],
    )

    // Create stable version of swipe handlers
    const handleSwipeLeft = useCallback(
        (options = {}) => {
            const detail = typeof options === 'string' ? { source: options } : options
            const { source = 'wheel-swipe', ...metricDetail } = detail

            if (useStore.getState().showRightSidebar) {
                useStore.getState().setShowRightSidebar(false)
            } else {
                slidePrev({ source, gesture: 'wheel', ...metricDetail })
            }
        },
        [slidePrev],
    )

    const handleSwipeRight = useCallback(
        (options = {}) => {
            const { allowSidebarClose = true, source = 'wheel-swipe', ...metricDetail } = options

            if (allowSidebarClose && closeSidebarOnSwipe && useStore.getState().showSidebar) {
                handleSidebarClose()
                return
            }

            slideNext({ source, gesture: 'wheel', ...metricDetail })
        },
        [closeSidebarOnSwipe, handleSidebarClose, slideNext],
    )

    useEffect(() => {
        setCloseSidebarOnBlur(isMobile)
    }, [isMobile, setCloseSidebarOnBlur])

    useEffect(() => {
        if (!isInitialized) return
        if (isMobile) return
        if (!window.gon?.currentUser) return

        // Load saved sidebar preference for desktop mode; default open in one-panel mode when unset.
        const spaceOptions = window.gon?.currentUser?.space_options ?? {}
        const preference = spaceOptions.sidebarOpen ?? spaceOptions.onePanelSideBar
        const hasPreference = typeof preference === 'string'
        const shouldOpen = hasPreference ? preference === 'open' : onePanel
        setShowSidebar(shouldOpen, { persist: false })
    }, [isInitialized, isMobile, onePanel, setShowSidebar])

    const isEventOverLeftSidebar = useCallback(event => {
        const path =
            typeof event.composedPath === 'function' ? event.composedPath() : event.path || []
        const matchesPath = path.some(el => {
            if (!(typeof HTMLElement !== 'undefined' && el instanceof HTMLElement)) return false
            return el.dataset?.swipeRegion === 'left-sidebar' || el.id === 'sidebar'
        })
        if (matchesPath) return true

        const sidebarEl = document.getElementById('sidebar')
        if (!sidebarEl || typeof event.clientX !== 'number' || typeof event.clientY !== 'number') {
            return false
        }
        const rect = sidebarEl.getBoundingClientRect()
        return (
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom
        )
    }, [])

    // Create the wheel handler
    const handleWheel = useCallback(
        ev => {
            const { deltaX, deltaY } = ev
            const isHorizontalScroll = Math.abs(deltaX) > Math.abs(deltaY)

            if (!isHorizontalScroll) return

            const overControlTarget = isEventOverControlTarget(ev)

            if (overControlTarget && Math.abs(deltaX) > 2) {
                ev.preventDefault()
                ev.stopPropagation()
                const storeState = useStore.getState()
                if (deltaX > 0) {
                    if (storeState.closeSidebarOnSwipe && storeState.showSidebar) {
                        handleSidebarClose()
                    } else if (storeState.showRightSidebar) {
                        setShowRightSidebar(false)
                    }
                } else if (deltaX < 0) {
                    if (storeState.showRightSidebar) {
                        setShowRightSidebar(false)
                    } else if (storeState.closeSidebarOnSwipe && !storeState.showSidebar) {
                        handleSidebarOpen()
                    }
                }
                prevDeltaXVal = Math.abs(deltaX)
                isSwipingFlag = false
                isSwipingLeftFlag = false
                isSwipingRightFlag = false
                return
            }
            if (isEventInsidePanelSwipeExclusion(ev)) {
                prevDeltaXVal = 0
                isSwipingFlag = false
                isSwipingLeftFlag = false
                isSwipingRightFlag = false
                return
            }

            if (useStore.getState().showSidebar && isEventOverLeftSidebar(ev)) {
                prevDeltaXVal = 0
                isSwipingFlag = false
                isSwipingLeftFlag = false
                isSwipingRightFlag = false
                return
            }

            const isIncreasingDelta = Math.abs(deltaX) - Math.abs(prevDeltaXVal) > 1
            const isDecreasingDelta = prevDeltaXVal - Math.abs(deltaX) > 1

            prevDeltaXVal = Math.abs(deltaX)

            if (isDecreasingDelta || Math.abs(deltaX) <= 1) {
                isSwipingFlag = false
                isSwipingLeftFlag = false
                isSwipingRightFlag = false
            } else if (isIncreasingDelta && !isSwipingFlag) {
                isSwipingFlag = true
                const inputDetail = getPanelNavInputDetail(ev)

                if (deltaX < 0 && !isSwipingLeftFlag) {
                    ev.preventDefault()
                    ev.stopPropagation()
                    isSwipingLeftFlag = true
                    handleSwipeLeft({ source: 'wheel-swipe', ...inputDetail })
                } else if (deltaX > 0 && !isSwipingRightFlag) {
                    ev.preventDefault()
                    ev.stopPropagation()
                    isSwipingRightFlag = true
                    const allowSidebarClose = isEventOverLeftSidebar(ev)
                    handleSwipeRight({ allowSidebarClose, source: 'wheel-swipe', ...inputDetail })
                }
            }
        },
        [
            handleSwipeLeft,
            handleSwipeRight,
            handleSidebarClose,
            handleSidebarOpen,
            isEventOverLeftSidebar,
            isEventOverControlTarget,
        ],
    )

    // Use throttled version
    const handleTouchPadSwipe = useMemo(
        () => throttle(handleWheel, 50, { leading: true, trailing: false }),
        [handleWheel],
    )

    // Set up wheel handler when numPanels changes or handler updates
    useEffect(() => {
        if (isMobile || !handleTouchPadSwipe) return

        let timeoutId
        let isCleanedUp = false

        // Keep stable references to the slide event handlers so we can .off() safely
        const slideStartHandlerRef = { current: null }
        const slideEndHandlerRef = { current: null }

        const setupWheelHandler = () => {
            if (isCleanedUp) return

            if (!swiperRef.current) {
                timeoutId = setTimeout(setupWheelHandler, 100)
                return
            }

            // Clean up existing handlers
            document.removeEventListener('wheel', handleTouchPadSwipe)
            try {
                if (slideStartHandlerRef.current) {
                    swiperRef.current.on &&
                        swiperRef.current.off &&
                        swiperRef.current.off(
                            'slideChangeTransitionStart',
                            slideStartHandlerRef.current,
                        )
                }
                if (slideEndHandlerRef.current) {
                    swiperRef.current.on &&
                        swiperRef.current.off &&
                        swiperRef.current.off(
                            'slideChangeTransitionEnd',
                            slideEndHandlerRef.current,
                        )
                }
            } catch (e) {
                console.warn('Error cleaning slide handlers', e)
            }

            // Set up new handlers
            const onSlideStart = ev => {
                document.removeEventListener('wheel', handleTouchPadSwipe)
            }
            slideStartHandlerRef.current = onSlideStart
            try {
                swiperRef.current.on &&
                    swiperRef.current.on('slideChangeTransitionStart', onSlideStart)
            } catch (e) {
                console.warn('Error adding slideChangeTransitionStart handler', e)
            }

            const onSlideEnd = ev => {
                document.addEventListener('wheel', handleTouchPadSwipe, { passive: false })
            }
            slideEndHandlerRef.current = onSlideEnd
            try {
                swiperRef.current.on && swiperRef.current.on('slideChangeTransitionEnd', onSlideEnd)
            } catch (e) {
                console.warn('Error adding slideChangeTransitionEnd handler', e)
            }

            document.addEventListener('wheel', handleTouchPadSwipe, { passive: false })
        }

        setupWheelHandler()

        // Cleanup
        return () => {
            isCleanedUp = true
            if (timeoutId) clearTimeout(timeoutId)
            if (!isMobile && handleTouchPadSwipe) {
                document.removeEventListener('wheel', handleTouchPadSwipe)
                if (swiperRef.current) {
                    try {
                        if (slideStartHandlerRef.current) {
                            swiperRef.current.off &&
                                swiperRef.current.off(
                                    'slideChangeTransitionStart',
                                    slideStartHandlerRef.current,
                                )
                        }
                        if (slideEndHandlerRef.current) {
                            swiperRef.current.off &&
                                swiperRef.current.off(
                                    'slideChangeTransitionEnd',
                                    slideEndHandlerRef.current,
                                )
                        }
                    } catch (e) {
                        console.warn('Error cleaning slide handlers on unmount', e)
                    }
                }
                isSwipingFlag = false
                prevDeltaXVal = 0
            }
        }
    }, [handleTouchPadSwipe, numPanels])

    useEffect(() => {
        let channel
        const currentUser = gon?.currentUser
        if (currentUser) {
            channel = CableApp.cable.subscriptions.create(
                withChannelAuth({ channel: 'UserChannel', userId: currentUser.id }),
                {
                    received: async data => {
                        if (data.message === 'history updated') {
                            const stateQuestHistory = useStore.getState().questHistory
                            const mergedQuest = cacheQuestPreservingPendingOptimisticAnswers(
                                data.quest,
                                { source: 'UserChannel.history' },
                            )
                            useStore.getState().set({
                                questHistory: uniqBy([mergedQuest, ...stateQuestHistory], 'id'),
                            })
                        }
                    },
                },
            )
        }
        return () => {
            if (channel && channel.unsubscribe) {
                channel.unsubscribe()
            }
        }
    }, [])

    useEffect(() => {
        const user = gon.currentUser
        let channel
        if (user) {
            channel = CableApp.cable.subscriptions.create(
                withChannelAuth({ channel: 'UserChannel', userId: user.id }),
                {
                    received: async data => {
                        if (data.message === 'new quest notification added') {
                            // TODO PANEL REFACTOR
                            // show new noti
                        }
                    },
                },
            )
        }

        return () => {
            if (channel && channel.unsubscribe) {
                channel.unsubscribe()
            }
        }
    }, [])

    const isExpoAndroid = window.KNOVIGATOR_IS_MOBILE && /Android/i.test(navigator.userAgent)
    const isOldMobile = isMobile && !window.visualViewport

    useEffect(() => {
        // Use geometry only as a fallback. A native-capable Android shell already publishes the
        // authoritative open/closed transition; also tracking every resize sample redraws the
        // panel tree throughout the IME animation.
        if (
            shouldUseViewportKeyboardFallback({
                isExpoAndroid,
                isOldMobile,
                hasNativeKeyboardState,
            })
        ) {
            const keyboardCheck = () => {
                const diff = Math.max(0, initialInnerHeight - window.innerHeight)
                const isOpen = diff > 100
                setViewportKeyboardOpen(isOpen)
                setViewportKeyboardHeight(isOpen ? diff : 0)
            }

            window.addEventListener('resize', keyboardCheck)

            return () => {
                if (keyboardCheck) window.removeEventListener('resize', keyboardCheck)
            }
        }
    }, [hasNativeKeyboardState, isExpoAndroid, isOldMobile])

    const lastActiveIndexRef = useRef(null)

    useEffect(() => {
        if (
            shouldObserveViewportKeyboard({
                isExpoAndroid,
                isOldMobile,
                isMobile,
                hasVisualViewport: Boolean(window.visualViewport),
                hasNativeKeyboardState,
            })
        ) {
            const visualViewport = window.visualViewport
            let initialVisualViewportHeight = visualViewport.height
            // Android: what the keyboard's arrival read, and the most it
            // occluded, for the earlier read of its departure
            // (resolveViewportKeyboardOpen).
            let otherWasOpen = false
            let peakOcclusion = 0
            // Compute iOS safe-area bottom once
            let iOSSafeAreaBottom = 0
            if (isIOS) {
                try {
                    const probe = document.createElement('div')
                    probe.style.paddingBottom = 'env(safe-area-inset-bottom)'
                    probe.style.position = 'absolute'
                    probe.style.visibility = 'hidden'
                    document.body.appendChild(probe)
                    const cs = getComputedStyle(probe)
                    iOSSafeAreaBottom = parseFloat(cs.paddingBottom) || 0
                    document.body.removeChild(probe)
                } catch (e) {}
            }

            const isEditableElementFocused = () => {
                const activeEl = document.activeElement
                if (!(activeEl instanceof HTMLElement)) return false
                const tagName = activeEl.tagName?.toLowerCase()

                return (
                    activeEl.isContentEditable ||
                    tagName === 'input' ||
                    tagName === 'textarea' ||
                    activeEl.getAttribute?.('role') === 'textbox'
                )
            }

            const keyboardCheck = throttle(
                () => {
                    // iOS: rely on layout-vs-visual height difference OR pageTop > 0
                    // This avoids toolbar-induced baseline artifacts and catches cases
                    // where only the offset changes during keyboard animation.
                    const iOSDiffRaw = Math.max(0, window.innerHeight - visualViewport.height)
                    const iOSDiff = Math.max(0, iOSDiffRaw - iOSSafeAreaBottom)
                    const iOSHasFocusedEditor = isEditableElementFocused()
                    const iOSOpen =
                        iOSDiff > 20 || (iOSHasFocusedEditor && (visualViewport.pageTop || 0) > 0)
                    // Detection above stays on the raw occlusion; only the reserve
                    // nets out WebKit's caret-reveal shift, which the panel's own
                    // scroll would otherwise count a second time.
                    const iOSReserve = resolveIosKeyboardReserve({
                        occlusion: iOSDiff,
                        pageShift: visualViewport.offsetTop,
                    })

                    // Android/others: baseline diff vs initial visual viewport height
                    const otherDiff = Math.max(
                        0,
                        initialVisualViewportHeight - visualViewport.height,
                    )
                    const otherOpen = resolveViewportKeyboardOpen({
                        wasOpen: otherWasOpen,
                        occlusion: otherDiff,
                        peakOcclusion,
                    })
                    peakOcclusion = otherOpen ? Math.max(peakOcclusion, otherDiff) : 0
                    otherWasOpen = otherOpen

                    const isKeyboardOpenNow = isIOS ? iOSOpen : otherOpen
                    const effectiveHeight = isIOS ? iOSReserve : otherDiff

                    setViewportKeyboardOpen(isKeyboardOpenNow)
                    setViewportKeyboardHeight(currentHeight =>
                        resolveViewportKeyboardHeight({
                            currentHeight,
                            nextHeight: effectiveHeight,
                            keyboardOpen: isKeyboardOpenNow,
                            // Android layout ignores the sampled height (Panel's keyboard spacer
                            // stays at zero), so pin it while open instead of re-rendering the
                            // panel tree for every intermediate IME-animation sample.
                            ignoreHeightChanges: isAndroid,
                        }),
                    )
                },
                // Android's viewport shrinks and grows in step with the IME,
                // and the panel foot follows the viewport, so the first sample
                // past a threshold is already the right call: taking it at
                // once, and the next within a couple of frames, lets the
                // navbar go and the composer take the keyboard's edge as it
                // arrives, and the foot start back as it leaves, not a long
                // interval later. Samples that change nothing cost no render.
                // iOS keeps the trailing sample only, at the longer interval —
                // its geometry settles over the slide and mid-slide samples
                // misread the reserve.
                isIOS ? 100 : 40,
                { leading: !isIOS, trailing: true },
            )

            // Listen to both resize and scroll; iOS sometimes only fires scroll on visual viewport changes
            visualViewport.addEventListener('resize', keyboardCheck)
            if (isIOS) visualViewport.addEventListener('scroll', keyboardCheck)

            // Run once to initialize
            keyboardCheck()

            return () => {
                keyboardCheck.cancel()
                visualViewport.removeEventListener('resize', keyboardCheck)
                if (isIOS) visualViewport.removeEventListener('scroll', keyboardCheck)
            }
        }
    }, [hasNativeKeyboardState, isExpoAndroid, isOldMobile])

    const toggleSidebar = () => {
        const newState = !showSidebar
        // Track left sidebar open/close
        window.logEv(newState ? 'OPEN_LEFT_SIDEBAR' : 'CLOSE_LEFT_SIDEBAR', {
            trigger_source: 'hamburger_button',
        })
        setShowSidebar(newState)
    }

    const blurSidebar = ev => {
        if (!showSidebar) return
        if (!closeSidebarOnBlur) return

        const sidebarEl = document.getElementById('sidebar')
        if (!sidebarEl) return

        if (sidebarEl.contains(ev.target)) return

        toggleSidebar()
    }

    const panels = useStore(store => store.panels)
    const panelList = panels.state
    const storeCenterIndex = useStore(state => state.centerIndex)
    const { getTransformStyle } = useAnimatedSlide()
    const panelRowShift = useStore(store => store.panels.rowShift)
    useLayoutEffect(() => {
        if (!panelRowShift) return
        const { token } = panelRowShift
        // Opening centre and closing also slide the view in a layout effect, which
        // re-renders the windows' centre/side state synchronously. A microtask runs
        // after that settles and still before paint.
        queueMicrotask(() => {
            if (useStore.getState().panels.rowShift?.token !== token) return
            playPanelRowShift(panelRowShift, () => {
                useStore.getState().set(draft => {
                    if (draft.panels.rowShift?.token === token) draft.panels.rowShift = null
                })
            })
        })
    }, [panelRowShift])
    const { updateCenteredPanelId } = useSlideEffects()

    // Use store centerIndex if available, otherwise calculate from ref
    const centerIndex =
        storeCenterIndex ??
        (isMobile || onePanel ? swiperRef.current?.activeIndex : swiperRef.current?.activeIndex + 1)
    const panelAnimationStyles = panelList.map((_, index) => {
        // In onePanel mode, don't apply any transform styles
        if (onePanel && !isMobile) {
            return {}
        }
        return getTransformStyle(index, centerIndex)
    })

    // Record the centered panel so the Android hardware back button has a
    // temporal trail to walk back down — panel navigation writes history but
    // nothing ever read it, so there was no record of where the user came from.
    // Entries dedupe by panelId, so the repeated re-centering from mode
    // switches and back-driven slides collapses onto the existing entry.
    useEffect(() => {
        recordCenteredPanelVisit(getPanelBackStack(), panelList[centerIndex], pathFromFilter)
    }, [centerIndex, panelList])

    // Under the dock, the fold follows its keyboard: present, and not being
    // dismissed where the dismissal's start already moves the pill.
    const foldKeyboardOpen = centerDocked ? dockSnapshot.foldKeyboardOpen : keyboardOpen
    useEffect(() => {
        const wasKeyboardOpen = previousKeyboardOpenRef.current
        previousKeyboardOpenRef.current = foldKeyboardOpen
        if (
            !shouldBlurNewAnswerAfterKeyboardClose({
                isMobile: isMobileSurface,
                wasKeyboardOpen,
                keyboardOpen: foldKeyboardOpen,
            })
        ) {
            return
        }

        // The keyboard going away is the reader's "done": a phone's folded
        // composer pill goes back to its one line as well as losing focus.
        blurFocusedNewAnswer({ force: true, foldCompact: true })
        try {
            const activeIx = swiperRef?.current?.activeIndex ?? 0
            const panel = panelList?.[activeIx]
            panel?.consumePendingScrollToBottom?.()
            panel?.consumePendingRevealNewAnswer?.()
        } catch (e) {}
    }, [foldKeyboardOpen, panelList, isMobileSurface])

    // When keyboard opens on Android, reveal the NewAnswer if the active panel requested it
    useEffect(() => {
        if (!shouldRevealNewAnswerOnKeyboardOpen({ keyboardOpen, isAndroid })) return
        try {
            const swiper = swiperRef?.current
            const activeIx = swiper?.activeIndex ?? 0
            const panel = panelList?.[activeIx]
            if (!panel || panel.empty) return
            // First handle explicit bottom scroll requests from NewAnswer focus
            if (panel.hasPendingScrollToBottom && panel.hasPendingScrollToBottom()) {
                let attempts = 0
                const maxAttempts = 6
                const interval = 80
                const attemptBottom = () => {
                    attempts += 1
                    try {
                        if (isIMEComposing && isIMEComposing()) {
                            if (attempts < maxAttempts) return setTimeout(attemptBottom, interval)
                            panel.consumePendingScrollToBottom?.()
                            return
                        }
                        panel.scrollToBottom?.()
                        panel.consumePendingScrollToBottom?.()
                    } catch (e) {}
                }
                const t = setTimeout(attemptBottom, 60)
                return () => clearTimeout(t)
            }

            // Otherwise handle reveal requests
            if (panel.hasPendingRevealNewAnswer && panel.hasPendingRevealNewAnswer()) {
                // Try multiple times as the keyboard animates and placeholder settles
                let attempts = 0
                const maxAttempts = 8
                const interval = 80

                const attemptReveal = () => {
                    attempts += 1
                    try {
                        let targetEl = null
                        try {
                            const activeNewAnswerRef = getEditorRef('activeNewAnswerRef')
                            const sideNewAnswerRef = getEditorRef('sideNewAnswerRef')
                            const compRef = activeNewAnswerRef?.current || sideNewAnswerRef?.current
                            targetEl = compRef?.containerRef?.current || null
                        } catch (e) {}

                        // Fallback: query within this panel's scroll container
                        if (!targetEl) {
                            try {
                                const sc = panel.getScrollContainer && panel.getScrollContainer()
                                const all = sc?.querySelectorAll?.('.new-answer-comp')
                                if (all && all.length) targetEl = all[all.length - 1]
                            } catch (e) {}
                        }

                        const sc = panel.getScrollContainer && panel.getScrollContainer()
                        if (panel.scrollToRevealElement && targetEl && sc) {
                            // Perform minimal reveal without smooth animation to avoid IME contention
                            panel.scrollToRevealElement(targetEl, {
                                bottomMargin: 30,
                                topMargin: 30,
                                smooth: false,
                            })
                            // Check if it's now within margins; if not, retry
                            const containerRect = sc.getBoundingClientRect()
                            const rect = targetEl.getBoundingClientRect()
                            const okBottom =
                                rect.bottom <= containerRect.top + sc.clientHeight - 30 + 1
                            const okTop = rect.top >= containerRect.top + 30 - 1
                            if (okBottom && okTop) {
                                panel.consumePendingRevealNewAnswer?.()
                                return
                            }
                        }
                    } catch (e) {}
                    if (attempts < maxAttempts) setTimeout(attemptReveal, interval)
                    else panel.consumePendingRevealNewAnswer?.()
                }

                const t = setTimeout(attemptReveal, 80)
                return () => clearTimeout(t)
            }
        } catch (e) {}
    }, [keyboardOpen, keyboardHeight, panelList])

    //console.log('RENDER PANELCONTROLLER-------------------------------------')
    //console.log('panel state', panels)
    //console.log('active index', swiperRef.current?.activeIndex)
    //console.log('starting index', startingIndex)
    //console.log('center index', centerIndex)

    useLayoutEffect(
        function handleMobileSwipe() {
            if (isMobile || onePanel) {
                let touchStartX = null
                let touchStartY = null
                let touchStartTime = null
                let touchStartedOverLeftSidebar = false
                let touchStartedOverControlRegion = false

                const handleTouchStart = event => {
                    touchStartedOverLeftSidebar = false
                    touchStartedOverControlRegion = false

                    // Check if the touch started on or inside a model-viewer element
                    const path = event.composedPath?.() || event.path || []
                    const isModelViewerTouch = path.some(
                        el => el.tagName?.toLowerCase() === 'model-viewer',
                    )
                    if (isModelViewerTouch) {
                        return // Don't track this touch, let model-viewer handle it
                    }

                    touchStartX = event.touches[0].clientX
                    touchStartY = event.touches[0].clientY
                    touchStartTime = Date.now()

                    const startedInsideControlPath = path.some(el => {
                        if (!(typeof HTMLElement !== 'undefined' && el instanceof HTMLElement))
                            return false
                        return (
                            el.dataset?.swipeRegion === 'sidebar-control' ||
                            el.id === CONTROL_SWIPE_TARGET_ID
                        )
                    })
                    touchStartedOverControlRegion =
                        startedInsideControlPath ||
                        isPointInsideControlTarget(touchStartX, touchStartY)

                    const sidebarTouchedViaPath = path.some(el => {
                        const isHTMLElement =
                            typeof HTMLElement !== 'undefined' && el instanceof HTMLElement
                        if (!isHTMLElement) return false
                        const datasetRegion = el.dataset?.swipeRegion
                        return (
                            datasetRegion === 'left-sidebar' ||
                            el.id === 'sidebar' ||
                            el.closest?.('[data-swipe-region="left-sidebar"]')
                        )
                    })

                    touchStartedOverLeftSidebar =
                        useStore.getState().showSidebar && sidebarTouchedViaPath
                }

                const handleTouchMove = event => {
                    // Check if the touch is on or inside a model-viewer element
                    const path = event.composedPath?.() || event.path || []
                    const isModelViewerTouch = path.some(
                        el => el.tagName?.toLowerCase() === 'model-viewer',
                    )
                    if (isModelViewerTouch) {
                        return // Let model-viewer handle the touch
                    }

                    if (!touchStartX || !touchStartY) return

                    const touchMoveX = event.touches[0].clientX
                    const touchMoveY = event.touches[0].clientY
                    const deltaX = touchMoveX - touchStartX
                    const deltaY = Math.abs(touchMoveY - touchStartY)
                    const timeDelta = Date.now() - touchStartTime

                    // Calculate velocity (pixels per millisecond)
                    const velocity = Math.abs(deltaX) / timeDelta

                    // Adjust thresholds based on velocity
                    const SWIPE_THRESHOLD = velocity > 0.5 ? 50 : 100 // More sensitive for fast swipes
                    const VERTICAL_LIMIT = 30 // Stricter vertical limit

                    if (Math.abs(deltaX) > SWIPE_THRESHOLD && deltaY < VERTICAL_LIMIT) {
                        const storeState = useStore.getState()

                        if (touchStartedOverControlRegion) {
                            if (deltaX > 0 && storeState.showRightSidebar) {
                                setShowRightSidebar(false)
                                touchStartX = null
                            } else if (
                                deltaX < 0 &&
                                storeState.closeSidebarOnSwipe &&
                                storeState.showSidebar
                            ) {
                                handleSidebarClose()
                                touchStartX = null
                            } else if (
                                deltaX > 0 &&
                                storeState.closeSidebarOnSwipe &&
                                !storeState.showSidebar
                            ) {
                                handleSidebarOpen()
                                touchStartX = null
                            } else if (
                                // The mirror of the left sidebar: swiping left
                                // along the navbar pulls in the right one.
                                deltaX < 0 &&
                                storeState.closeSidebarOnSwipe &&
                                !storeState.showSidebar &&
                                !storeState.showRightSidebar
                            ) {
                                window.logEv('OPEN_RIGHT_SIDEBAR', {
                                    trigger_source: 'navbar_swipe',
                                })
                                setShowRightSidebar(true)
                                touchStartX = null
                            }
                            touchStartedOverControlRegion = false
                            return
                        }

                        if (
                            deltaX < 0 &&
                            storeState.closeSidebarOnSwipe &&
                            touchStartedOverLeftSidebar &&
                            storeState.showSidebar
                        ) {
                            handleSidebarClose()
                            touchStartX = null // Prevent multiple triggers
                            touchStartedOverLeftSidebar = false
                        }
                        if (deltaX > 0 && storeState.showRightSidebar) {
                            setShowRightSidebar(false)
                            touchStartX = null // Prevent multiple triggers
                        }
                    }
                }

                const handleTouchEnd = () => {
                    touchStartX = null
                    touchStartY = null
                    touchStartTime = null
                    touchStartedOverLeftSidebar = false
                    touchStartedOverControlRegion = false
                }

                document.addEventListener('touchstart', handleTouchStart)
                // We do not call preventDefault in this handler; keeping it passive avoids blocking native
                // scrolling (notably on iOS) inside overflow containers like the left sidebar.
                document.addEventListener('touchmove', handleTouchMove, { passive: true })
                document.addEventListener('touchend', handleTouchEnd)

                return () => {
                    document.removeEventListener('touchstart', handleTouchStart)
                    document.removeEventListener('touchmove', handleTouchMove)
                    document.removeEventListener('touchend', handleTouchEnd)
                }
            }
        },
        [onePanel],
    )

    const handleUserControlClick = useCallback(() => {
        // Track right sidebar open
        window.logEv('OPEN_RIGHT_SIDEBAR', {
            trigger_source: 'user_icon',
        })
        setShowRightSidebar(true)
    }, [])

    // Memoize Swiper initialization and event handlers
    const handleSwiperInit = useCallback(
        swiper => {
            // @ts-expect-error - swiperRef is mutable
            swiperRef.current = swiper
            window.swiper = swiper
            lastActiveIndexRef.current = swiper.activeIndex
            deliverSwiperTouchEndFromElement(swiper)

            // Add touch handler to prevent swipes on model-viewer
            swiper.el.addEventListener(
                'touchstart',
                e => {
                    const path = e.composedPath?.() || e.path || []
                    const isModelViewerTouch = path.some(
                        el => el.tagName?.toLowerCase() === 'model-viewer',
                    )
                    if (isModelViewerTouch) {
                        swiper.allowTouchMove = false
                        e.stopPropagation()
                    }
                },
                { passive: true },
            )

            swiper.el.addEventListener(
                'touchend',
                () => {
                    swiper.allowTouchMove = true
                },
                { passive: true },
            )

            // The wheel handler will be set up by the effect that watches numPanels changes
        },
        [handleTouchPadSwipe],
    )

    const handleSlideChange = useCallback(async () => {
        const swiper = swiperRef.current
        if (!swiper) return

        // Note: slideNavState is updated in onActiveIndexChange which fires first

        const ix = isMobile || onePanel ? swiper.activeIndex : swiper.activeIndex + 1
        let panels = useStore.getState().panels.state
        let panel = panels[ix]
        const filter = panel?.filter
        if (filter && isFilterReadyForPanelHistory(filter)) {
            const quest = filter.questId ? cache.getCachedQuest(filter.questId) : null
            const shouldTrackQuestView =
                filter.questId && quest ? isServerBackedViewableQuest(quest) : false
            const shouldPersistUserSpaceOptions = !filter.questId || !isLocalCachedQuest(quest)

            const panelId = panel.panelId
            const path = pathFromFilter(filter)
            history.pushState({ centerIndex: ix, panelId, filter }, '', path)
            // Panel navigation bypasses React Router and writes history
            // directly, so its router subscription cannot update the title.
            setDocumentTitleForPath(path)
            markPanelNavRouteChange({
                activeIndex: swiper.activeIndex,
                centerIndex: ix,
                panelId,
                path,
                filter,
                shouldTrackQuestView,
                isMobile,
                onePanel,
            })
            if (filter.questId && quest) {
                markPanelNavQuestSettled({
                    panelId,
                    questId: filter.questId,
                    hasDisplayQuest: true,
                    source: 'route-cache',
                })
            }
            const sideEffectId = latestPanelNavSideEffectIdRef.current + 1
            latestPanelNavSideEffectIdRef.current = sideEffectId
            panelNavSideEffectsRef.current?.enqueue({
                id: sideEffectId,
                path,
                filter: { ...filter },
                shouldPersistUserSpaceOptions,
                shouldTrackQuestView,
            })
        }
    }, [onePanel])

    const handleSlideChangeTransitionStart = useCallback(() => {
        markPanelNavTransitionStart(
            getPanelNavDetail(swiperRef.current, onePanel, {
                source: 'slideChangeTransitionStart',
            }),
        )
        if (!isMobile) return
        blurFocusedNewAnswer()
    }, [onePanel])

    const handleSlideChangeTransitionEnd = useCallback(
        swiper => {
            completeOnePanelPrewarm()
            markPanelNavTransitionEnd(
                getPanelNavDetail(swiper, onePanel, {
                    source: 'slideChangeTransitionEnd',
                }),
            )
            panelNavSideEffectsRef.current?.flushAfterTransition()
        },
        [onePanel],
    )

    const handleSwiperTouchStart = useCallback(
        (swiper, event) => {
            if (isEventInsidePanelSwipeExclusion(event)) return

            // A rightward gesture is the common back path and its parked panel must
            // paint before Swiper exposes any part of the adjacent slide. If the
            // gesture moves left, sliderMove immediately retargets the prewarm.
            prewarmOnePanelGesture(swiper, 'prev')
            startPanelNavMetric(
                'swiper-touch',
                getPanelNavDetail(swiper, onePanel, {
                    gesture: 'touch',
                    eventType: event?.type,
                    touches: event?.touches?.length,
                    ...getPanelNavInputDetail(event),
                }),
            )
        },
        [onePanel],
    )

    const handleSwiperSliderMove = useCallback(
        swiper => {
            prewarmOnePanelGesture(swiper)
            markPanelNavFirstPanelMove(
                getPanelNavDetail(swiper, onePanel, {
                    source: 'sliderMove',
                }),
            )
        },
        [onePanel],
    )

    const handleSwiperTouchEnd = useCallback(() => {
        finishOnePanelGesturePrewarm()
    }, [])

    const handleSwiperSetTranslate = useCallback(
        (swiper, translate) => {
            markPanelNavFirstPanelMove(
                getPanelNavDetail(swiper, onePanel, {
                    source: 'setTranslate',
                    translate,
                }),
            )
        },
        [onePanel],
    )

    // Memoize panel transformation logic
    const handlePanelTransforms = useCallback(
        (activeIndex, lastActiveIndex, animate = true) => {
            // Get fresh numPanels value from store to avoid closure issues
            const currentNumPanels = useStore.getState().numPanels
            const isOnePanel = currentNumPanels === 1

            if (isMobile || isOnePanel) return

            const panels = useStore.getState().panels.state
            console.log('HANDLE PANEL TRANSFORMS:', {
                activeIndex,
                lastActiveIndex,
                animate,
                panelsLength: panels.length,
            })

            if (lastActiveIndex !== null && Math.abs(activeIndex - lastActiveIndex) === 1) {
                // Optimization: If we're only moving by one panel, just update the changing panels
                const lastCenterIndex = lastActiveIndex + 1
                const newCenterIndex = activeIndex + 1

                console.log('TRANSFORM - Moving center from', lastCenterIndex, 'to', newCenterIndex)

                // Transform the new center panel to BIG
                if (panels[newCenterIndex]) {
                    console.log(
                        'TRANSFORM - Setting BIG on panel',
                        newCenterIndex,
                        panels[newCenterIndex].panelId,
                    )
                    transformTo(BIG, panels[newCenterIndex], animate ? undefined : 'none')
                }

                // Transform the old center panel to SMALL
                if (lastCenterIndex !== newCenterIndex && panels[lastCenterIndex]) {
                    console.log(
                        'TRANSFORM - Setting SMALL on panel',
                        lastCenterIndex,
                        panels[lastCenterIndex].panelId,
                    )
                    transformTo(SMALL, panels[lastCenterIndex], animate ? undefined : 'none')
                }

                // Also ensure the edge panels have correct transforms
                if (activeIndex < lastActiveIndex) {
                    // Moved left, ensure leftmost panel is SMALL
                    if (panels[activeIndex]) {
                        console.log(
                            'TRANSFORM - Ensuring SMALL on left panel',
                            activeIndex,
                            panels[activeIndex].panelId,
                        )
                        transformTo(SMALL, panels[activeIndex], animate ? undefined : 'none')
                    }
                } else {
                    // Moved right, ensure rightmost panel is SMALL
                    if (panels[activeIndex + 2]) {
                        console.log(
                            'TRANSFORM - Ensuring SMALL on right panel',
                            activeIndex + 2,
                            panels[activeIndex + 2].panelId,
                        )
                        transformTo(SMALL, panels[activeIndex + 2], animate ? undefined : 'none')
                    }
                }
            } else {
                //console.log('TRANSFORM - Initial setup (lastActiveIndex is null)')
                // Initial load or edge case.
                if (panels[activeIndex]) {
                    //console.log(
                    //    'TRANSFORM - Setting SMALL on panel',
                    //    activeIndex,
                    //    panels[activeIndex].panelId,
                    //)
                    transformTo(SMALL, panels[activeIndex], animate ? undefined : 'none')
                }
                if (panels[activeIndex + 1]) {
                    //console.log(
                    //    'TRANSFORM - Setting BIG on panel',
                    //    activeIndex + 1,
                    //    panels[activeIndex + 1].panelId,
                    //)
                    transformTo(BIG, panels[activeIndex + 1], animate ? undefined : 'none')
                }
                if (panels[activeIndex + 2]) {
                    console.log(
                        'TRANSFORM - Setting SMALL on panel',
                        activeIndex + 2,
                        panels[activeIndex + 2].panelId,
                    )
                    transformTo(SMALL, panels[activeIndex + 2], animate ? undefined : 'none')
                }
            }
        },
        [onePanel],
    )

    // Keep onActiveIndexChange as a regular function since it needs fresh state
    const onActiveIndexChange = swiper => {
        // Ignore slide changes during mode switching
        if (isModeSwitchingRef.current) {
            console.log('ON ACTIVE INDEX CHANGE - IGNORED (mode switching in progress)')
            return
        }

        // Get fresh numPanels value from store to avoid closure issues
        const currentNumPanels = useStore.getState().numPanels
        const isOnePanel = currentNumPanels === 1

        // Additional check: if swiper's slidesPerView doesn't match our mode, this is a stale event
        const expectedSlidesPerView = isOnePanel ? 1 : 3
        if (!isMobile && swiper.params.slidesPerView !== expectedSlidesPerView) {
            console.log('ON ACTIVE INDEX CHANGE - IGNORED (stale event, slidesPerView mismatch)')
            return
        }

        const centerIndex = isMobile || isOnePanel ? swiper.activeIndex : swiper.activeIndex + 1
        const previousActiveIndex = lastActiveIndexRef.current

        if (previousActiveIndex !== swiper.activeIndex) {
            pausePanelMediaLeavingSwiperViewport(previousActiveIndex, swiper.activeIndex)
        }

        //console.log('ON ACTIVE INDEX CHANGE:', {
        //    swiperActiveIndex: swiper.activeIndex,
        //    centerIndex,
        //    currentNumPanels,
        //    isOnePanel,
        //    lastActiveIndexRef: lastActiveIndexRef.current,
        //    isManualSlide: isManualSlide.current,
        //})

        // Track one-panel state for stable mode switching
        if (!isMobile && isOnePanel) {
            const pList = useStore.getState().panels.state
            const centerId = pList[centerIndex]?.panelId
            lastOnePanelActiveRef.current = swiper.activeIndex
            lastOnePanelCenterIdRef.current = centerId
            //console.log('TRACK ONE-PANEL POSITION', {
            //    activeIndex: swiper.activeIndex,
            //    centerIndex,
            //    centerId,
            //})
        }

        updateCenteredPanelId(centerIndex)
        useStore.getState().set({ activeIndex: swiper.activeIndex, centerIndex })

        // Apply transforms in 3-panel mode
        if (!isMobile && !isOnePanel) {
            // Only run transforms if this was triggered by next/prev buttons
            if (isManualSlideActive()) {
                console.log('ON ACTIVE INDEX CHANGE - Manual slide, animating transforms')
                // Call with animate=true to animate the scale.
                handlePanelTransforms(swiper.activeIndex, lastActiveIndexRef.current, true)
                consumeManualSlideFlag()
            } else {
                console.log('ON ACTIVE INDEX CHANGE - Auto slide, instant transforms')
                // Call with animate=false for instant scale.
                handlePanelTransforms(swiper.activeIndex, lastActiveIndexRef.current, false)
            }
        }

        // Update the lastActiveIndexRef
        lastActiveIndexRef.current = swiper.activeIndex
    }

    // Memoize generatePreview to prevent recreation on every render
    const generatePreview = useMemo(() => {
        return ({ itemType, item, style }) => {
            let width = isMobile ? '100%' : '33%'
            return (
                <div
                    style={Object.assign({}, style, { zIndex: 2000, width, height: '100%' })}
                    className={'noselect row'}
                >
                    <div className="col-md-12">
                        <Answer {...item} />
                    </div>
                </div>
            )
        }
    }, []) // Only depends on isMobile which is constant

    const isIOSStandalone = useMemo(() => {
        if (!isIOS) return false
        try {
            const legacyStandalone = window.navigator?.standalone === true
            const media = window.matchMedia && window.matchMedia('(display-mode: standalone)')
            const mediaStandalone = !!media && media.matches
            return legacyStandalone || mediaStandalone
        } catch (e) {
            return false
        }
    }, [isIOS])
    const isiOSWithHomeIndicator = useMemo(() => {
        if (!isMobileIosBrowser) return false
        const { screen } = window
        if (!screen) return false
        const longestSide = Math.max(screen.width || 0, screen.height || 0)
        // iPhone X and newer (with a home indicator) report >= 812 logical pixels on the long edge
        return longestSide >= 812
    }, [isMobileIosBrowser])
    // bottom_editor: what the composer pill clears at a phone's bottom edge — the
    // same signals the navbar layers.
    const bottomEditorInset = useMemo(
        () => resolveBottomEditorInset({ isIOSStandalone, isiOSWithHomeIndicator }),
        [isIOSStandalone, isiOSWithHomeIndicator],
    )
    // Whether the signed-in navbar is on screen: exactly the render condition
    // below. A phone's pill stands on it, so the panel foot has to know.
    // fixed_header, desktop: a panel header under the top-right corner controls
    // clears their real width (the wallet balance makes it vary), published as a
    // variable the place bar reads. Desktop only, and written only when it
    // changes: a custom property on <html> restyles the whole page, which on a
    // phone ran on every swipe between a thread and a channel.
    const rightCornerRef = useRef(null)
    useLayoutEffect(() => {
        if (isMobile) return
        const el = rightCornerRef.current
        const root = document.documentElement
        if (!el || typeof ResizeObserver === 'undefined') return
        let published = null
        const publish = () => {
            const width = el.offsetWidth
            if (width === published) return
            published = width
            root.style.setProperty('--corner-controls-right-width', `${width}px`)
        }
        publish()
        const observer = new ResizeObserver(publish)
        observer.observe(el)
        return () => observer.disconnect()
    }, [showRightSidebar, hideCornersForThread])

    const bottomNavigationVisible =
        Boolean(gon.currentUser) && !navigationHidden && !isConsoleOpen && !showSidebar
    const controlSwipeOverlayStyle = useMemo(() => {
        if (!controlSwipeRect) return { display: 'none' }
        const width = Math.max(0, controlSwipeRect.right - controlSwipeRect.left)
        const height = Math.max(0, controlSwipeRect.bottom - controlSwipeRect.top)
        return {
            top: `${Math.max(0, controlSwipeRect.top)}px`,
            left: `${Math.max(0, controlSwipeRect.left)}px`,
            width: `${width}px`,
            height: `${height}px`,
        }
    }, [controlSwipeRect])

    const effectiveTheme = resolveEffectiveTheme(appearanceTheme)
    const panelThemeVariant = `${isMobile ? 'mobile' : 'desktop'}-${effectiveTheme}`

    return (
        <>
            <div
                className={cn(
                    styles.panelControllerComp,
                    onePanel && !isMobile && styles.onePanelBackground,
                )}
                data-testid="app-shell-root"
                data-panel-theme={panelThemeVariant}
                onClick={blurSidebar}
                onTouchStart={blurSidebar}
            >
                <LeftSidebar
                    showSidebar={showSidebar}
                    closeOnSwipe={isMobile}
                    onePanel={onePanel}
                    toggleOnePanel={toggleNumPanels}
                    toggleSidebar={toggleSidebar}
                />

                {/* Floating header nav controls are rendered per-panel in PanelContainer */}

                {gon.currentUser && (
                    <RightSidebar
                        show={showRightSidebar}
                        onClose={() => setShowRightSidebar(false)}
                        user={gon.currentUser}
                    />
                )}

                {fixedHeaderEnabled() && !isMobile && (
                    <PanelEdgeChevrons onPrev={() => slidePrev()} onNext={() => slideNext()} />
                )}

                {/* fixed_header, one-panel desktop: a hairline across the top of
                    the whole page, which the column's side borders run up to. */}
                {fixedHeaderEnabled() && !isMobile && onePanel && (
                    <div className={styles.fixedHeaderTopLine} aria-hidden="true" />
                )}

                <div
                    className={styles.sidebarControls}
                    // fixed_header, desktop: three-panel windows float 10px below
                    // the top edge, so the corner controls drop onto the centre
                    // line of the windows' header rows.
                    data-fixed-header={
                        fixedHeaderEnabled()
                            ? isMobile
                                ? 'phone'
                                : onePanel
                                ? 'column'
                                : 'window'
                            : undefined
                    }
                >
                    <div
                        id={CONTROL_SWIPE_TARGET_ID}
                        className={styles.sidebarControlSwipeTarget}
                        data-swipe-region="sidebar-control"
                        aria-hidden="true"
                        style={controlSwipeOverlayStyle}
                    />
                    <div
                        className={cn(
                            styles.leftCornerContainer,
                            isMobile && styles.mobileLeftCornerContainer,
                        )}
                    >
                        <div
                            className={cn(
                                styles.sidebarButtonContainer,
                                isMobile && styles.mobileSidebarButtonContainer,
                            )}
                        >
                            {!showSidebar && !hideCornersForThread && (
                                <SidebarButton
                                    showSidebar={showSidebar}
                                    toggleSidebar={toggleSidebar}
                                    customClass="sidebar-button"
                                />
                            )}
                            {false && !showSidebar && <TDP />}
                            {/* Mobile inline slide-left control to the RIGHT of the sidebar button */}
                            {isMobile && !fixedHeader && canSlidePrev() && (
                                <WithTooltip asChild tip="Slide left.">
                                    <div
                                        className={styles.mobileSlideLeftInline}
                                        data-testid="previous-panel"
                                        onClick={e => {
                                            e.stopPropagation()
                                            slidePrev(e)
                                        }}
                                    >
                                        <ArrowLeft size={18} />
                                    </div>
                                </WithTooltip>
                            )}
                        </div>
                    </div>

                    {!showRightSidebar && !hideCornersForThread && (
                        <div
                            ref={rightCornerRef}
                            className={cn(
                                styles.rightCornerContainer,
                                isMobile && styles.mobileRightCornerContainer,
                            )}
                        >
                            <div
                                className={cn(
                                    styles.userStreamContainer,
                                    isMobile && styles.mobileUserStreamContainer,
                                )}
                            >
                                {false && (
                                    <MobileConsole
                                        buttonClassName=""
                                        onVisibilityChange={visible => setIsConsoleOpen(visible)}
                                    />
                                )}
                                {/* fixed_header moves the balance into the Wallet menu item
                                    on phones; desktop keeps it in the corner. */}
                                {gon?.currentUser && (!fixedHeader || !isMobile) && (
                                    <WalletBalanceBadge />
                                )}
                                {gon?.currentUser ? (
                                    <WithTooltip tip="Open profile menu.">
                                        <UserControl
                                            user={gon.currentUser}
                                            contextStyles={cn(styles.userIcon)}
                                            onClick={handleUserControlClick}
                                        />
                                    </WithTooltip>
                                ) : (
                                    <WithTooltip asChild tip="Join to reply.">
                                        <div
                                            className={cn(styles.loginIcon)}
                                            onClick={() =>
                                                openAuthModal('login', 'top_right_user_icon')
                                            }
                                            data-testid="top-right-login-button"
                                        >
                                            <i className={cn('fa fa-user')} />
                                        </div>
                                    </WithTooltip>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Navigation arrows for 3-panel mode. fixed_header puts back/forward
                    in the focused window's header row instead. */}
                {!isMobile && numPanels === 3 && !fixedHeader && (
                    <>
                        <WithTooltip asChild tip="Slide left.">
                            <div
                                className={styles.threePanelNavLeft}
                                onClick={e => {
                                    if (canSlidePrev()) {
                                        setManualSlide(true)
                                        slidePrev(e)
                                    }
                                }}
                                style={{
                                    position: 'fixed',
                                    // When sidebar is open, position arrow at left edge of center panel
                                    // In 3-panel mode, center panel starts at 33.33vw
                                    // Arrow (32px) should be 10px to the left of center panel edge
                                    left: showSidebar ? 'calc(33.33vw - 33px)' : '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 9000,
                                    cursor: canSlidePrev() ? 'pointer' : 'default',
                                    transition: 'left 0.3s ease, opacity 0.2s ease',
                                    opacity: canSlidePrev() ? 1 : 0,
                                    pointerEvents: canSlidePrev() ? 'auto' : 'none',
                                }}
                            >
                                <ArrowLeft size={18} />
                            </div>
                        </WithTooltip>
                        <WithTooltip asChild tip="Slide right.">
                            <div
                                className={styles.threePanelNavRight}
                                onClick={e => {
                                    if (canSlideNext()) {
                                        setManualSlide(true)
                                        slideNext(e)
                                    }
                                }}
                                style={{
                                    position: 'fixed',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 1000,
                                    cursor: canSlideNext() ? 'pointer' : 'default',
                                    transition: 'opacity 0.2s ease',
                                    opacity: canSlideNext() ? 1 : 0,
                                    pointerEvents: canSlideNext() ? 'auto' : 'none',
                                }}
                            >
                                <ArrowRight size={18} />
                            </div>
                        </WithTooltip>
                    </>
                )}

                {/* Slide controls moved to PanelHeader */}

                <ErrorBoundary label="DndProvider">
                    {/* @ts-expect-error - DndProvider typing issue */}
                    <DndProvider backend={MultiBackend} options={HTML5toTouch}>
                        <GlobalAnswersDndProvider>
                            <ErrorBoundary label="Swiper">
                                <div
                                    ref={panelControllerRef}
                                    style={{ overflow: 'hidden', height: '100dvh' }}
                                    className={cn(onePanel && !isMobile && styles.onePanelMode)}
                                >
                                    {isInitialized && (
                                        <Swiper
                                            modules={[Virtual]}
                                            // Mode dictates presentation; do not derive SPV from panels array
                                            slidesPerView={isMobile || onePanel ? 1 : 3}
                                            initialSlide={(() => {
                                                const sc = useStore.getState().centerIndex
                                                if (isMobile || onePanel) {
                                                    if (
                                                        Number.isInteger(sc) &&
                                                        panelList?.[sc] &&
                                                        !panelList[sc].empty
                                                    ) {
                                                        return sc
                                                    }
                                                    const firstNonEmpty = (
                                                        panelList || []
                                                    ).findIndex(p => !p.empty)
                                                    return firstNonEmpty >= 0 ? firstNonEmpty : 0
                                                }
                                                // In 3-up, align left-of-center to current centerIndex if known
                                                if (Number.isInteger(sc)) return Math.max(0, sc - 1)
                                                return startingIndex + 1
                                            })()}
                                            onSwiper={handleSwiperInit}
                                            speed={
                                                onePanel && !isMobile
                                                    ? SWIPE_SPEED_SLOW
                                                    : SWIPE_SPEED_FAST
                                            }
                                            preventClicksPropagation={true}
                                            preventClicks={false}
                                            touchMoveStopPropagation={true}
                                            simulateTouch={isMobile}
                                            passiveListeners={false}
                                            noSwiping={true}
                                            noSwipingSelector={PANEL_SWIPE_EXCLUSION_SELECTOR}
                                            {...((isMobile || onePanel) &&
                                                slideUpdates === 0 && {
                                                    style: { overflow: 'initial' },
                                                })}
                                            onSlideChange={handleSlideChange}
                                            onSlideChangeTransitionStart={
                                                handleSlideChangeTransitionStart
                                            }
                                            onSlideChangeTransitionEnd={
                                                handleSlideChangeTransitionEnd
                                            }
                                            onTouchStart={handleSwiperTouchStart}
                                            onTouchEnd={handleSwiperTouchEnd}
                                            onSliderMove={handleSwiperSliderMove}
                                            onSetTranslate={handleSwiperSetTranslate}
                                            onActiveIndexChange={onActiveIndexChange}
                                            virtual={{
                                                enabled: true,
                                                addSlidesBefore: 1,
                                                addSlidesAfter: 1,
                                            }}
                                        >
                                            {panelList.map((panel, index) => {
                                                const panelAnimationStyle =
                                                    panelAnimationStyles[index]
                                                //console.log('PANEL ANIMATION STYLE', index, panelAnimationStyle)
                                                return (
                                                    <SwiperSlide
                                                        key={panel.panelId}
                                                        virtualIndex={index}
                                                    >
                                                        <div
                                                            data-slide-panel-id={panel.panelId}
                                                            className={cn(
                                                                'panel-slide-wrapper',
                                                                onePanel &&
                                                                    !isMobile &&
                                                                    styles.onePanelSlide,
                                                            )}
                                                            style={panelAnimationStyle}
                                                        >
                                                            <PanelContainer
                                                                panel={panel}
                                                                index={index}
                                                                isEmpty={panel.empty}
                                                                // Need this to pass through the context to the explicit visibility styles in child components.
                                                                hide={
                                                                    panelAnimationStyle?.display ===
                                                                    'none'
                                                                }
                                                                panelTheme={panelThemeVariant}
                                                                keyboardOpen={
                                                                    bottomEditorDock &&
                                                                    bottomEditorPanelIds.has(
                                                                        panel.panelId,
                                                                    )
                                                                        ? dockSnapshot.foldKeyboardOpen
                                                                        : footKeyboardOpen
                                                                }
                                                                dock={Boolean(bottomEditorDock)}
                                                                keyboardHeight={footKeyboardHeight}
                                                                keyboardSettling={keyboardSettling}
                                                                keyboardPhase={keyboardPhase}
                                                                rememberedKeyboardHeight={
                                                                    lastKeyboardHeightRef.current
                                                                }
                                                                nativeKeyboardResize={
                                                                    viewportResizes
                                                                }
                                                                bottomEditorInset={
                                                                    bottomEditorInset
                                                                }
                                                                bottomEditorMobileSurface={
                                                                    isMobileSurface
                                                                }
                                                                bottomNavigationVisible={
                                                                    bottomNavigationVisible
                                                                }
                                                                onBottomEditorPresenceChange={
                                                                    handleBottomEditorPresenceChange
                                                                }
                                                            >
                                                                {panel.empty ? (
                                                                    <EmptyPanel />
                                                                ) : panel.filter?.history ? (
                                                                    <HistoryPanel />
                                                                ) : (
                                                                    <Panel
                                                                        keyboardOpen={
                                                                            footKeyboardOpen
                                                                        }
                                                                        keyboardHeight={
                                                                            footKeyboardHeight
                                                                        }
                                                                    />
                                                                )}
                                                            </PanelContainer>
                                                        </div>
                                                    </SwiperSlide>
                                                )
                                            })}
                                        </Swiper>
                                    )}
                                </div>
                            </ErrorBoundary>
                        </GlobalAnswersDndProvider>

                        <Preview generator={generatePreview} />
                    </DndProvider>
                </ErrorBoundary>

                <TreechatTooltip className={styles.highZIndexTooltip} />
                <TitleNotificationWidget />
            </div>

            <div
                ref={controlContainerRef}
                data-bottom-controls
                data-swipe-region="sidebar-control"
                className={cn(
                    styles.controlContainer,
                    isMobile && styles.mobileControlContainer,
                    isMobileAppOrAndroid && styles.mobileControlContainerAppOrAndroid,
                    isAndroid && styles.mobileControlContainerAndroid,
                )}
            >
                {gon.currentUser &&
                    (!newButtonHidden || !navigationHidden) &&
                    !isConsoleOpen &&
                    //!onePanel &&
                    !showSidebar && (
                        <>
                            {!newButtonHidden && (
                                <div
                                    data-new-quest-button
                                    className={cn(
                                        styles.newQuestFloatingContainer,
                                        isMobileAppOrAndroid &&
                                            styles.mobileNewQuestFloatingContainer,
                                        isMobile &&
                                            !isMobileAppOrAndroid &&
                                            styles.mobileBrowserNewQuestFloatingContainer,
                                    )}
                                >
                                    <NewQuestButton
                                        panel={panelList[centerIndex]}
                                        filter={panelList[centerIndex]?.filter}
                                        place="top"
                                        append={true}
                                    >
                                        <NewQuestButtonUIMainWrapper
                                            label="New"
                                            showLabel
                                            showIcon={false}
                                            neutral={Boolean(
                                                panelList[centerIndex]?.filter?.questId,
                                            )}
                                            contextStyles={cn(
                                                styles.newQuestFloatingButton,
                                                panelList[centerIndex]?.filter?.questId &&
                                                    styles.newQuestFloatingButtonThread,
                                                isOrdinalsMintEnabled(
                                                    panelList[centerIndex]?.filter,
                                                ) && styles.newQuestFloatingButtonMint,
                                            )}
                                            filter={
                                                panelList[centerIndex]?.filter || { private: true }
                                            }
                                            user={gon.currentUser}
                                        />
                                    </NewQuestButton>
                                </div>
                            )}

                            {!navigationHidden && (
                                <Control
                                    showFilterOptions={showFilterOptions}
                                    setShowFilterOptions={setShowFilterOptions}
                                    isIOSStandalone={isIOSStandalone}
                                    isiOSWithHomeIndicator={isiOSWithHomeIndicator}
                                    returning={!centerDocked && keyboardSettling}
                                />
                            )}
                        </>
                    )}

                {!gon.currentUser && !bottomNavigationHidden && !isConsoleOpen && !onePanel && (
                    <PublicControl
                        showFilterOptions={showFilterOptions}
                        setShowFilterOptions={setShowFilterOptions}
                    />
                )}
            </div>

            <AutocompletePopup />
        </>
    )
})

// Slide controls have been moved to PanelHeader component

function TitleNotificationWidget() {
    const numNoti = useStore(state => state.numNoti)
    useEffect(() => {
        // Decorate the page title rather than replacing it: the server-rendered
        // per-page <title> is what Google indexes, and clobbering it here put
        // every public page in the index as plain "Treechat".
        setTitleNotificationCount(numNoti)
    }, [numNoti])
    return <></>
}
